import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';

import { createPortal } from 'react-dom';

import { 
    CheckCircle, 
    Lock, 
    MoreVertical, 
    Check, 
    CheckCheck, 
    X,
    Megaphone,
    Target,
    User,
    Mail,
    MessageSquareReply,
    Clock,
    AlertCircle
} from 'lucide-react';

import '../../styles-wa/inbox_view.css';

import { getInboxConversations, getContactMessages, createMessage, createContact, markContactRead, markInboxRead, markInboxUnread, sendWhatsAppTemplate, deleteContact, updateContact, getApiErrorMessage, apiClient } from '../../api-wa';

import { useNotifications } from '../../Context/WaNotificationContext';

import { useConfig } from '../../Context/WaConfigContext';

import AddContactModal from '../../Components/WhatsApp/AddContactModal';

import Toast from '../../Components/WhatsApp/Toast';

import TemplateModal from '../../Components/WhatsApp/TemplateModal';

import MessageGroup from '../../Components/WhatsApp/MessageGroup';

import { COUNTRY_CODES } from './countryCodes';

import {

    formatSidebarTimestamp,

    formatMessageTime,

    getFirstUnreadMessageIndex,

} from './messageUtils';



const sortContactsByLatest = (contactList) => {

    return [...contactList].sort((a, b) => {

        const aTime = new Date(a.last_message_time || a.created_at || 0).getTime();

        const bTime = new Date(b.last_message_time || b.created_at || 0).getTime();

        return bTime - aTime;

    });

};



const sortMessagesByTime = (messageList) => {

    return [...messageList].sort((a, b) => {

        const aTime = new Date(a.created_at || a.timestamp || 0).getTime();

        const bTime = new Date(b.created_at || b.timestamp || 0).getTime();

        return aTime - bTime;

    });

};



const getContactIdentity = (contact) => contact?.id ?? contact?.phone_number ?? contact?.phone ?? null;



const upsertContactList = (contactList, contactUpdate, overrides = {}) => {

    if (!contactUpdate) return contactList;



    const identity = contactUpdate.id || contactUpdate.phone_number || contactUpdate.phone;

    if (!identity) return contactList;



    const nextList = [...contactList];

    const existingIndex = nextList.findIndex((item) => {

        const itemId = item.id;

        const itemPhone = item.phone_number || item.phone;



        if (contactUpdate.id && itemId && String(contactUpdate.id) === String(itemId)) return true;

        if (contactUpdate.phone_number && itemPhone && String(contactUpdate.phone_number) === String(itemPhone)) return true;

        if (contactUpdate.phone && itemPhone && String(contactUpdate.phone) === String(itemPhone)) return true;



        return false;

    });



    if (existingIndex >= 0) {

        const existing = nextList[existingIndex];

        // Don't overwrite a good name with 'Unknown'

        const mergedName = (contactUpdate.name && contactUpdate.name !== 'Unknown')

            ? contactUpdate.name

            : (existing.name || contactUpdate.name);



        let newUnreadCount = existing.unread_count ?? 0;

        if (overrides.unread_count !== undefined) {

            newUnreadCount = Number(overrides.unread_count);

        } else if (overrides.incrementUnread) {

            newUnreadCount += 1;

        } else if (contactUpdate.unread_count !== undefined) {

            newUnreadCount = Number(contactUpdate.unread_count);

        }



        nextList[existingIndex] = {

            ...existing,

            ...contactUpdate,

            ...overrides,

            name: mergedName,

            unread_count: newUnreadCount,

        };

    } else {

        let newUnreadCount = 0;

        if (overrides.unread_count !== undefined) {

            newUnreadCount = Number(overrides.unread_count);

        } else if (overrides.incrementUnread) {

            newUnreadCount = 1;

        } else if (contactUpdate.unread_count !== undefined) {

            newUnreadCount = Number(contactUpdate.unread_count);

        }



        nextList.unshift({

            ...contactUpdate,

            ...overrides,

            unread_count: newUnreadCount,

        });

    }



    return sortContactsByLatest(nextList);

};



const upsertMessageList = (messageList, messageUpdate) => {

    if (!messageUpdate || (!messageUpdate.id && !messageUpdate.provider_message_id)) {

        return [...messageList, messageUpdate];

    }

    const index = messageList.findIndex((item) => {

        if (messageUpdate.id && item.id && String(item.id) === String(messageUpdate.id)) return true;

        if (messageUpdate.provider_message_id && item.provider_message_id && String(item.provider_message_id) === String(messageUpdate.provider_message_id)) return true;

        return false;

    });

    

    if (index >= 0) {

        const newList = [...messageList];

        newList[index] = { ...newList[index], ...messageUpdate };

        return newList;

    }

    return [...messageList, messageUpdate];

};



// WhatsApp text formatting: *bold*, _italic_, ~strikethrough~

const formatWhatsAppText = (text) => {

    if (!text) return '';

    

    // First, handle WhatsApp markdown

    const parts = [];

    let remaining = text;

    const waRegex = /(\*[^\s*\n](?:[^*]*?[^\s*\n])?\*|_[^\s_\n](?:[^_]*?[^\s_\n])?_|~[^\s~\n](?:[^~]*?[^\s~\n])?~)/g;

    let lastIndex = 0;

    let match;

    while ((match = waRegex.exec(remaining)) !== null) {

        if (match.index > lastIndex) {

            parts.push(remaining.substring(lastIndex, match.index));

        }

        const raw = match[0];

        if (raw.startsWith('*') && raw.endsWith('*')) {

            parts.push(<strong key={`b-${match.index}`}>{raw.slice(1, -1)}</strong>);

        } else if (raw.startsWith('_') && raw.endsWith('_')) {

            parts.push(<em key={`i-${match.index}`}>{raw.slice(1, -1)}</em>);

        } else if (raw.startsWith('~') && raw.endsWith('~')) {

            parts.push(<s key={`s-${match.index}`}>{raw.slice(1, -1)}</s>);

        }

        lastIndex = match.index + raw.length;

    }

    if (lastIndex < remaining.length) {

        parts.push(remaining.substring(lastIndex));

    }



    // Now, handle URLs for each string part

    const finalParts = [];

    const urlRegex = /(https?:\/\/[^\s]+)/g;

    

    parts.forEach((part, i) => {

        if (typeof part === 'string') {

            let urlLastIndex = 0;

            let urlMatch;

            // Reset lastIndex for the regex (not always necessary depending on usage, but safe)

            urlRegex.lastIndex = 0; 

            while ((urlMatch = urlRegex.exec(part)) !== null) {

                if (urlMatch.index > urlLastIndex) {

                    finalParts.push(part.substring(urlLastIndex, urlMatch.index));

                }

                const url = urlMatch[0];

                finalParts.push(

                    <a 

                        key={`url-${i}-${urlMatch.index}`} 

                        href={url} 

                        target="_blank" 

                        rel="noopener noreferrer"

                        style={{ color: '#0066cc', textDecoration: 'underline' }}

                        onClick={(e) => e.stopPropagation()}

                    >

                        {url}

                    </a>

                );

                urlLastIndex = urlMatch.index + url.length;

            }

            if (urlLastIndex < part.length) {

                finalParts.push(part.substring(urlLastIndex));

            }

        } else {

            finalParts.push(part);

        }

    });



    return finalParts.length > 0 ? finalParts : text;

};



const getMediaTypeFromUrl = (url) => {

    if (!url) return null;

    if (url === 'embedded_template_media' || url.startsWith('meta_media_')) return null;

    // Lazily-fetched template media URLs (/templates/<id>/header-media/) carry no file

    // extension to detect from - the backend appends a ?type= hint instead, check that first.

    const typeHintMatch = url.match(/[?&]type=(image|video|document|audio)\b/);

    if (typeHintMatch) return typeHintMatch[1];

    const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase();

    if (cleanUrl.match(/\.(jpeg|jpg|gif|png|webp|svg|bmp)$/)) {

        return 'image';

    }

    if (cleanUrl.match(/\.(mp4|webm|ogg|mov|avi|flv)$/)) {

        return 'video';

    }

    if (cleanUrl.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv|zip|rar)$/)) {

        return 'document';

    }

    if (cleanUrl.match(/\.(mp3|wav|ogg|m4a|aac)$/)) {

        return 'audio';

    }

    if (url.startsWith('data:image')) return 'image';

    if (url.startsWith('data:video')) return 'video';

    if (url.startsWith('data:audio')) return 'audio';

    

    return 'image'; // default fallback for template media

};



const getRenderableMediaUrl = (url) => {

    if (!url) return '';

    if (url === 'embedded_template_media' || url.startsWith('meta_media_')) return '';

    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:')) {

        return url;

    }

    if (url.startsWith('/')) {

        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

        const apiBaseUrl = isLocal ? 'http://localhost:8000' : `${window.location.protocol}//hrmbackendapi.${window.location.hostname.replace('hrm.', '')}`;

        try {

            return `${new URL(apiBaseUrl).origin}${url}`;

        } catch {

            return url;

        }

    }

    return '';

};



// Sub-component for expandable text with "Read more" / "Show less"

const ExpandableMessageText = ({ text, hasMedia }) => {

    const [isExpanded, setIsExpanded] = useState(false);

    const limit = 180;

    

    if (!text) return null;

    

    // If there's no media, the text sits at the top where the 3-dot menu is.

    // We add padding-right so it doesn't overlap the absolute-positioned menu.

    const textStyle = !hasMedia ? { paddingRight: '28px' } : {};

    

    if (text.length <= limit) {

        return (

            <div className="message-text message-body" style={textStyle}>

                {formatWhatsAppText(text)}

            </div>

        );

    }

    

    return (

        <div className="message-text message-body" style={textStyle}>

            {formatWhatsAppText(isExpanded ? text : `${text.slice(0, limit)}...`)}

            <span 

                className="read-more-link" 

                onClick={(e) => { 

                    e.stopPropagation(); 

                    setIsExpanded(!isExpanded); 

                }}

            >

                {isExpanded ? ' Show less' : ' Read more'}

            </span>

        </div>

    );

};



const InboxView = ({ activeChatId: propActiveChatId, setActiveChatId: propSetActiveChatId }) => {

    const { notifications, markRead: markNotificationReadContext } = useNotifications();

    const { activeConfigId, configs, fetchConfigs } = useConfig();

    const activeConfig = useMemo(() => {
        return configs?.find(c => String(c.id) === String(activeConfigId));
    }, [configs, activeConfigId]);

    const isGlobalAiEnabled = activeConfig ? Boolean(activeConfig.is_ai_enabled) : false;

    const handleToggleGlobalAi = async () => {
        try {
            const nextVal = !isGlobalAiEnabled;
            await apiClient.patch(`/whatsapp/config/${activeConfigId}/`, {
                is_ai_enabled: nextVal
            });
            await fetchConfigs();
            setToast({ message: `Universal AI Auto-Reply turned ${nextVal ? 'ON' : 'OFF'}!`, type: 'success' });
        } catch (err) {
            setToast({ message: 'Failed to update universal AI setting.', type: 'error' });
        }
    };

    // State for contacts and messages from API

    const [contacts, setContacts] = useState([]);

    const [messages, setMessages] = useState([]);

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState(null);

    const [toast, setToast] = useState(null);

    const [activeChatId, _setActiveChatId] = useState(() => propActiveChatId || null);

    const [newMessage, setNewMessage] = useState('');

    const [currentPage, setCurrentPage] = useState(1);

    const [hasMoreMessages, setHasMoreMessages] = useState(true);

    const [loadingMore, setLoadingMore] = useState(false);



    const setActiveChatId = (id) => {

        _setActiveChatId(id);

        if (propSetActiveChatId) {

            propSetActiveChatId(id);

        }

    };



    useEffect(() => {

        if (propActiveChatId !== undefined && propActiveChatId !== activeChatId) {

            _setActiveChatId(propActiveChatId);

        }

    }, [propActiveChatId]);

    const [inputText, setInputText] = useState('');

    const [currentTime, setCurrentTime] = useState(new Date());

    const [searchTerm, setSearchTerm] = useState('');

    const [showContactPanel, setShowContactPanel] = useState(false);

    const [showAddContact, setShowAddContact] = useState(false);

    const [showContactMenu, setShowContactMenu] = useState(false);
    const [contextMenu, setContextMenu] = useState(null); // { x, y, contact }

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedContactIds, setSelectedContactIds] = useState(new Set());
    const [activeSourceTab, setActiveSourceTab] = useState('all'); // 'all' | 'campaign_replies' | 'leads' | 'direct'
    const [activeStatusFilter, setActiveStatusFilter] = useState('all'); // 'all' | 'unread' | 'needs_reply' | 'active_24h' | 'expired_24h'
    const [hideNoMessages, setHideNoMessages] = useState(false);

    const [isEditingName, setIsEditingName] = useState(false);

    const [editNameValue, setEditNameValue] = useState('');

    const [newContactName, setNewContactName] = useState('');

    const [newContactPhone, setNewContactPhone] = useState('');

    const [countryCode, setCountryCode] = useState('+91');



    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);

    const [messageInfo, setMessageInfo] = useState(null);

    const [isVideoLoading, setIsVideoLoading] = useState(false);

    const [isSending, setIsSending] = useState(false);

    const [selectedTemplateForSend, setSelectedTemplateForSend] = useState(null);

    const [connectionState, setConnectionState] = useState('connecting');

    const [unreadAnchorId, setUnreadAnchorId] = useState(null);

    const [pendingReadContact, setPendingReadContact] = useState(null);

    const [newMessagesNotice, setNewMessagesNotice] = useState(0);

    const [isNearBottom, setIsNearBottom] = useState(true);

    const fileInputRef = useRef(null);

    const [selectedFile, setSelectedFile] = useState(null);

    const [filePreviewUrl, setFilePreviewUrl] = useState(null);

    const messagesEndRef = useRef(null);

    const wsRef = useRef(null);

    const reconnectTimerRef = useRef(null);

    const reconnectAttemptsRef = useRef(0);

    const contactsRef = useRef([]);

    const activeChatIdRef = useRef(null);

    const isNearBottomRef = useRef(true);

    const readTimerRef = useRef(null);

    const scrollPositionsRef = useRef({});

    const longPressTimerRef = useRef(null);

    const longPressTriggeredRef = useRef(false);



    useEffect(() => {

        contactsRef.current = contacts;

    }, [contacts]);



    useEffect(() => {

        activeChatIdRef.current = activeChatId;

    }, [activeChatId]);



    useEffect(() => {

        return () => {

            if (readTimerRef.current) window.clearTimeout(readTimerRef.current);

        };

    }, []);



    useEffect(() => {

        const timer = setInterval(() => {

            setCurrentTime(new Date());

        }, 1000);

        return () => clearInterval(timer);

    }, []);



    // Keep messageInfo in sync with the messages list for real-time updates

    useEffect(() => {

        if (messageInfo) {

            const currentMsg = messages.find(m => String(m.id) === String(messageInfo.id));

            if (currentMsg && (

                currentMsg.status !== messageInfo.status ||

                currentMsg.delivered_at !== messageInfo.delivered_at ||

                currentMsg.read_at !== messageInfo.read_at

            )) {

                setMessageInfo(currentMsg);

            }

        }

    }, [messages, messageInfo]);



    const clearUnreadForContact = useCallback(async (contact) => {

        if (!contact?.phone_number) return;

        const readAt = new Date().toISOString();



        // Update individual messages to mark as read (for frontend UI)

        setMessages(prevMessages =>

            prevMessages.map(msg => ({

                ...msg,

                read: msg.direction === 'INCOMING' ? true : msg.read,

                is_read: msg.direction === 'INCOMING' ? true : msg.is_read,

                read_at: msg.direction === 'INCOMING' ? (msg.read_at || readAt) : msg.read_at,

                status: msg.direction === 'INCOMING' ? 'Read' : msg.status,

            }))

        );



        // Optimistically update contact unread count

        setContacts(prevContacts => sortContactsByLatest(

            prevContacts.map(item => (

                item.id === contact.id ? { ...item, unread_count: 0 } : item

            ))

        ));



        // Mark corresponding notifications for this contact as read (so they are cleared instantly)

        if (notifications && notifications.length > 0) {

            const normalizePhone = (p) => (p || '').replace(/\D/g, '').slice(-10);

            const contactPhone10 = normalizePhone(contact.phone_number || contact.phone);

            

            notifications.forEach(notif => {

                const notifPhone10 = normalizePhone(notif.contact_phone);

                if (!notif.is_read && contactPhone10 && notifPhone10 === contactPhone10) {

                    markNotificationReadContext(notif.id);

                }

            });

        }



        try {

            const updatedContact = await markContactRead(contact.phone_number);

            setContacts(prevContacts => sortContactsByLatest(

                prevContacts.map(item => (

                    item.id === updatedContact.id

                        ? { ...item, ...updatedContact, unread_count: 0 }

                        : item

                ))

            ));

        } catch (err) {

            console.error('Failed to mark conversation as read:', err);

        }

    }, [notifications, markNotificationReadContext]);



    const scheduleReadAfterScroll = useCallback((contact) => {

        if (!contact?.phone_number) return;

        if (readTimerRef.current) window.clearTimeout(readTimerRef.current);



        readTimerRef.current = window.setTimeout(async () => {

            await clearUnreadForContact(contact);

            setUnreadAnchorId(null);

            setPendingReadContact(null);

        }, 900);

    }, [clearUnreadForContact]);



    const scrollChatToBottom = useCallback((behavior = 'smooth') => {

        const container = messagesEndRef.current;

        if (!container) return;

        container.scrollTo({

            top: container.scrollHeight,

            behavior,

        });

    }, []);



    const handleChatScroll = useCallback(async () => {

        const container = messagesEndRef.current;

        if (!container) return;



        // Save current scroll position for active chat

        if (activeChatIdRef.current) {

            scrollPositionsRef.current[activeChatIdRef.current] = container.scrollTop;

        }



        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;

        const nextIsNearBottom = distanceFromBottom < 140;

        isNearBottomRef.current = nextIsNearBottom;

        setIsNearBottom(nextIsNearBottom);

        if (nextIsNearBottom) {

            setNewMessagesNotice(0);

        }



        // Handle infinite scrolling up for older messages

        if (container.scrollTop === 0 && hasMoreMessages && !loadingMore && activeChatIdRef.current) {

            setLoadingMore(true);

            try {

                const nextPage = currentPage + 1;

                const prevScrollHeight = container.scrollHeight;

                const newMessagesData = await getContactMessages(activeChatIdRef.current, nextPage);

                const results = Array.isArray(newMessagesData.results) ? newMessagesData.results : newMessagesData;

                

                if (results.length > 0) {

                    const newReversed = [...results].reverse();

                    setMessages(prev => [...newReversed, ...prev]);

                    setCurrentPage(nextPage);

                    setHasMoreMessages(!!newMessagesData.next);

                    

                    // Maintain scroll position without jumping

                    requestAnimationFrame(() => {

                        if (messagesEndRef.current) {

                            messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight - prevScrollHeight;

                        }

                    });

                } else {

                    setHasMoreMessages(false);

                }

            } catch (err) {

                console.error("Failed to fetch older messages", err);

            } finally {

                setLoadingMore(false);

            }

        }

    }, [currentPage, hasMoreMessages, loadingMore]);

    // Fetch contacts on component mount and config change

    useEffect(() => {

        if (!activeConfigId) {

            setLoading(false);

            setContacts([]);

            return;

        }



        const loadContacts = async () => {

            try {

                setLoading(true);

                const contactsData = await getInboxConversations();

                const sortedContacts = sortContactsByLatest(contactsData);

                const pendingContactId = localStorage.getItem('pendingInboxContactId');

                const pendingPhone = localStorage.getItem('pendingInboxPhone');

                const pendingPhone10 = pendingPhone ? pendingPhone.replace(/\D/g, '').slice(-10) : null;

                let pendingContact = pendingContactId

                    ? sortedContacts.find(contact => String(contact.id) === String(pendingContactId))

                    : pendingPhone10

                        ? sortedContacts.find(contact => (contact.phone_number || '').replace(/\D/g, '').slice(-10) === pendingPhone10 || (contact.phone || '').replace(/\D/g, '').slice(-10) === pendingPhone10)

                        : null;

                if (!pendingContact && pendingPhone) {
                    try {
                        const pendingName = localStorage.getItem('pendingInboxName') || 'Unknown';
                        let formattedPhone = pendingPhone.trim();
                        if (!formattedPhone.startsWith('+')) {
                            if (formattedPhone.length === 10) {
                                formattedPhone = `+91${formattedPhone}`;
                            } else {
                                formattedPhone = `+${formattedPhone}`;
                            }
                        }
                        const createdContact = await createContact({
                            name: pendingName,
                            phone_number: formattedPhone,
                            language: 'en',
                            attributes: {
                                language: 'en',
                                name: pendingName,
                                phone: formattedPhone,
                            },
                        });
                        sortedContacts.unshift(createdContact);
                        pendingContact = createdContact;
                    } catch (createErr) {
                        console.error("Failed to auto-create contact:", createErr);
                    }
                }

                const firstContact = pendingContact || sortedContacts[0];



                contactsRef.current = sortedContacts;

                setContacts(sortedContacts);

                localStorage.removeItem('pendingInboxContactId');

                localStorage.removeItem('pendingInboxPhone');

                localStorage.removeItem('pendingInboxName');



                // Check if current active chat is still valid in the new contacts list

                const isCurrentChatValid = activeChatIdRef.current && sortedContacts.some(c => String(c.id) === String(activeChatIdRef.current));



                if (!isCurrentChatValid) {
                    if (pendingContact) {
                        setActiveChatId(pendingContact.id);
                    } else {
                        setActiveChatId(null);
                        setMessages([]);
                    }
                }

                setError(null);

            } catch (err) {

                const errorMsg = getApiErrorMessage(err, 'Failed to load contacts');

                setError(errorMsg);

                setToast({ type: 'error', message: errorMsg });

            } finally {

                setLoading(false);

            }

        };

        loadContacts();

    }, [activeConfigId]);



    // Listen for custom notification click events to select chat in real-time

    useEffect(() => {

        const handlePendingNotification = (e) => {

            const phone = e.detail;

            if (phone) {

                const phone10 = phone.replace(/\D/g, '').slice(-10);

                const targetContact = contactsRef.current.find(contact => (contact.phone_number || '').replace(/\D/g, '').slice(-10) === phone10 || (contact.phone || '').replace(/\D/g, '').slice(-10) === phone10);

                if (targetContact) {

                    setActiveChatId(targetContact.id);

                }

            }

        };



        window.addEventListener('pendingNotificationChat', handlePendingNotification);

        return () => window.removeEventListener('pendingNotificationChat', handlePendingNotification);

    }, []);



    // Listen for custom event when all notifications are marked read from the dropdown

    useEffect(() => {

        const handleNotificationsMarkedRead = (e) => {

            const notifs = e.detail;

            if (!notifs || !Array.isArray(notifs)) return;

            

            // Extract unique phone numbers from unread notifications

            const unreadPhones = [...new Set(notifs.filter(n => !n.is_read && n.contact_phone).map(n => n.contact_phone))];

            if (unreadPhones.length === 0) return;



            // Trigger backend to mark them read

            unreadPhones.forEach(phone => {

                markContactRead(phone).catch(err => console.error('Failed to mark contact read from notifications:', err));

            });



            // Optimistically update the contacts list in the UI

            const unreadPhones10 = unreadPhones.map(p => p.replace(/\D/g, '').slice(-10));

            setContacts(prevContacts => prevContacts.map(contact => {

                const contactPhone10 = (contact.phone_number || contact.phone || '').replace(/\D/g, '').slice(-10);

                if (contactPhone10 && unreadPhones10.includes(contactPhone10)) {

                    return { ...contact, unread_count: 0 };

                }

                return contact;

            }));

        };



        window.addEventListener('notificationsMarkedRead', handleNotificationsMarkedRead);

        return () => window.removeEventListener('notificationsMarkedRead', handleNotificationsMarkedRead);

    }, []);



    // WebSocket Connection for Real-time updates

    useEffect(() => {

        let isCancelled = false;



        const getWebSocketUrl = () => {

            const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

            const wsHost = isLocal ? 'localhost:8000' : `hrmbackendapi.${window.location.hostname.replace('hrm.', '')}`;

            const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

            return `${wsProtocol}//${wsHost}/ws/crm/`;

        };



        const scheduleReconnect = () => {

            if (isCancelled) return;



            if (reconnectTimerRef.current) {

                clearTimeout(reconnectTimerRef.current);

            }



            const attempt = reconnectAttemptsRef.current;

            const delay = Math.min(1000 * (2 ** attempt), 10000);

            reconnectAttemptsRef.current += 1;



            reconnectTimerRef.current = window.setTimeout(() => {

                connectWebSocket();

            }, delay);

        };



        const updateContactFromRealtime = (contactUpdate, messageUpdate, forceUnreadZero = false) => {

            if (!contactUpdate && !messageUpdate) return;



            // Isolate real-time events by matching the active config ID

            const eventConfigId = contactUpdate?.whatsapp_config ?? messageUpdate?.contact_whatsapp_config;

            if (activeConfigId && eventConfigId && String(eventConfigId) !== String(activeConfigId)) {

                console.log(`[WS Ignored] Event for config ID ${eventConfigId}, but active config ID is ${activeConfigId}`);

                return;

            }



            // Use 'contact' as fallback for 'contact' since backend uses MessageSerializer.contact

            const contactId = contactUpdate?.id ?? messageUpdate?.contact ?? messageUpdate?.contact;

            const activeId = activeChatIdRef.current;

            const isActiveChat = activeId !== null && contactId !== null && String(activeId) === String(contactId);



            const getSummary = (msg) => {

                if (!msg) return '';

                const text = msg.text || msg.caption || msg.message || '';

                if (text.trim()) return text;

                if (msg.template_name) return `Template: ${msg.template_name}`;

                if (msg.type && msg.type !== 'text') return `[${msg.type.charAt(0).toUpperCase() + msg.type.slice(1)}]`;

                return '';

            };



            const lastMessageText = messageUpdate ? getSummary(messageUpdate) : (contactUpdate?.last_message || contactUpdate?.lastMessage || '');

            const lastMessageTime = messageUpdate?.timestamp || messageUpdate?.created_at || contactUpdate?.last_message_time || contactUpdate?.updated_at;

            const incomingActiveMessage = isActiveChat && messageUpdate?.direction === 'INCOMING';

            const shouldKeepUnread = incomingActiveMessage && !isNearBottomRef.current;

            

            let unreadCount = undefined;

            let incrementUnread = false;



            if (forceUnreadZero || (isActiveChat && !shouldKeepUnread)) {

                unreadCount = 0;

            } else if (contactUpdate?.unread_count !== undefined) {

                unreadCount = Number(contactUpdate.unread_count);

            } else if (!isActiveChat) {

                const isBotOrCustomerMessage = messageUpdate?.direction === 'INCOMING' || 

                                               messageUpdate?.sender === 'bot' || 

                                               messageUpdate?.sender === 'ai' || 

                                               messageUpdate?.sender === 'system' ||

                                               messageUpdate?.sender === 'workflow';

                if (isBotOrCustomerMessage) {

                    incrementUnread = true;

                }

            }



            if (messageUpdate && isActiveChat) {

                setMessages((prevMessages) => upsertMessageList(prevMessages, messageUpdate));

                

                // If scroll is pinned to bottom, automatically scroll to show the new message (bot, AI, customer, etc.)

                if (isNearBottomRef.current) {

                    window.setTimeout(() => {

                        scrollChatToBottom('smooth');

                        if (messageUpdate.direction === 'INCOMING') {

                            clearUnreadForContact(contactUpdate || {

                                id: contactId,

                                phone_number: messageUpdate?.contact_phone || messageUpdate?.contact_phone || contactUpdate?.phone_number,

                            });

                        }

                    }, 80);

                } else if (messageUpdate.direction === 'INCOMING') {

                    // Show "New Messages Ã¢— —œ" indicator if scrolled up

                    setNewMessagesNotice((count) => count + 1);

                }

            }



            if (contactUpdate || messageUpdate) {

                setContacts((prevContacts) => {

                    const fallbackContact = { id: contactId };

                    const phone = messageUpdate?.contact_phone || messageUpdate?.contact_phone || contactUpdate?.phone_number;

                    if (phone) fallbackContact.phone_number = phone;



                    const nextList = upsertContactList(prevContacts, contactUpdate || fallbackContact, {

                        last_message: lastMessageText,

                        last_message_time: lastMessageTime,

                        unread_count: unreadCount,

                        incrementUnread,

                    });

                    // Force re-sort to ensure latest activity is on top

                    return sortContactsByLatest(nextList);

                });

            }



        };



        const updateMessagesStatus = (data, fullMessage = null) => {

            const messageId = fullMessage?.id || data?.id || data?.message_id;

            if (!messageId) return;



            console.log('--- Real-time Status Update ---', { messageId, status: fullMessage?.status || data.status });



            setMessages((prevMessages) => prevMessages.map((messageItem) => {

                if (String(messageItem.id) !== String(messageId)) {

                    return messageItem;

                }



                return {

                    ...messageItem,

                    ...fullMessage,

                    status: fullMessage?.status || data.status || messageItem.status,

                };

            }));

        };



        const connectWebSocket = () => {

            if (isCancelled) return;



            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {

                wsRef.current.close();

            }



            const ws = new WebSocket(getWebSocketUrl());

            wsRef.current = ws;

            setConnectionState('connecting');



            ws.onopen = () => {

                reconnectAttemptsRef.current = 0;

                setConnectionState('connected');

            };



            ws.onmessage = (event) => {

                let parsedData = null;



                try {

                    parsedData = JSON.parse(event.data);

                    console.log('WS Received:', parsedData);

                } catch (parseError) {

                    return;

                }



                const realtimeEvent = parsedData?.event ? parsedData : parsedData?.message?.event ? parsedData.message : null;

                if (!realtimeEvent?.event) return;



                // Handle both 'contact' and 'contact' keys from backend

                const contactData = realtimeEvent.contact || realtimeEvent.contact;

                const messageData = realtimeEvent.message;



                if (realtimeEvent.event === 'message_status') {

                    updateMessagesStatus(realtimeEvent.data || {}, realtimeEvent.message);

                    return;

                }



                if (realtimeEvent.event === 'message_read' || realtimeEvent.event === 'unread_updated' || realtimeEvent.event === 'contact_updated') {

                    updateContactFromRealtime(contactData, messageData, true);

                    return;

                }



                if (realtimeEvent.event === 'new_message' || realtimeEvent.event === 'message_sent') {

                    updateContactFromRealtime(contactData, messageData);

                }

            };



            ws.onerror = () => {

                setConnectionState('error');

            };



            ws.onclose = () => {

                if (isCancelled) return;

                setConnectionState('reconnecting');

                scheduleReconnect();

            };

        };



        connectWebSocket();



        return () => {

            isCancelled = true;

            if (reconnectTimerRef.current) {

                clearTimeout(reconnectTimerRef.current);

            }

            if (wsRef.current) {

                wsRef.current.close();

            }

        };

    }, [activeChatIdRef, contactsRef, isNearBottomRef, activeConfigId]);



    // Background polling fallback to auto-refresh inbox conversations & active chat messages every 3 seconds

    useEffect(() => {

        if (!activeConfigId) return;



        let isPolling = false;

        const pollInboxUpdates = async () => {

            if (isPolling) return;

            isPolling = true;

            try {

                // 1. Refresh conversations list

                const latestContactsData = await getInboxConversations();

                if (latestContactsData && Array.isArray(latestContactsData)) {

                    const sortedContacts = sortContactsByLatest(latestContactsData);

                    contactsRef.current = sortedContacts;

                    setContacts(sortedContacts);

                }



                // 2. Refresh active chat messages if a contact is selected

                const currentActiveId = activeChatIdRef.current;

                if (currentActiveId) {

                    const latestMsgsResponse = await getContactMessages(currentActiveId, 1);

                    const fetchedMsgs = Array.isArray(latestMsgsResponse?.results)

                        ? latestMsgsResponse.results

                        : (Array.isArray(latestMsgsResponse) ? latestMsgsResponse : []);

                            

                            if (fetchedMsgs.length > 0) {

                                setMessages(prevMsgs => {

                                    let hasChanges = false;

                                    const msgMap = new Map();

                                    prevMsgs.forEach(m => msgMap.set(String(m.id), m));

                                    

                                    fetchedMsgs.forEach(m => {

                                        const key = String(m.id);

                                        if (!msgMap.has(key)) {

                                            msgMap.set(key, m);

                                            hasChanges = true;

                                        } else {

                                            const existing = msgMap.get(key);

                                            if (existing.status !== m.status || existing.read_at !== m.read_at) {

                                                msgMap.set(key, m);

                                                hasChanges = true;

                                            }

                                        }

                                    });



                                    if (hasChanges) {

                                        const updatedList = Array.from(msgMap.values());

                                        return sortMessagesByTime(updatedList);

                                    }

                                    return prevMsgs;

                                });

                            }

                }

            } catch (err) {

                // Ignore silent polling network errors

            } finally {

                isPolling = false;

            }

        };



        const pollInterval = setInterval(pollInboxUpdates, 3000);

        return () => clearInterval(pollInterval);

    }, [activeConfigId]);



    // Handle mobile/browser suspension by reconnecting and refreshing data on visibility change

    useEffect(() => {

        const handleVisibilityChange = () => {

            if (document.visibilityState === 'visible') {

                // 1. Force reconnect WebSocket if it exists

                if (wsRef.current) {

                    wsRef.current.close(); // The onclose handler will schedule a reconnect

                }



                // 2. Refresh contacts to catch any missed updates

                getInboxConversations().then(contactsData => {

                    setContacts(sortContactsByLatest(contactsData));

                    contactsRef.current = contactsData;

                }).catch(err => console.error('Error refreshing contacts on visibility change', err));



                // 3. Refresh active chat messages if one is open

                if (activeChatIdRef.current) {

                    getContactMessages(activeChatIdRef.current, 1).then(messagesData => {

                        const results = Array.isArray(messagesData.results) ? messagesData.results : messagesData;

                        const reversedMessages = [...results].reverse();

                        setMessages(reversedMessages);

                        setCurrentPage(1);

                        setHasMoreMessages(!!messagesData.next);

                        setTimeout(() => scrollChatToBottom('auto'), 100);

                    }).catch(err => console.error('Error refreshing messages on visibility change', err));

                }

            }

        };



        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);

    }, [scrollChatToBottom]);



    // Load messages when active chat changes

    useEffect(() => {

        if (activeChatId) {

            let isCurrent = true;

            const loadMessages = async () => {

                const selectedContact = contactsRef.current.find(contact => String(contact.id) === String(activeChatId));

                if (selectedContact && selectedContact.whatsapp_config && String(selectedContact.whatsapp_config) !== String(activeConfigId)) {

                    console.log(`[Messages load bypassed] Contact ${activeChatId} belongs to config ${selectedContact.whatsapp_config}, active is ${activeConfigId}`);

                    return;

                }



                try {

                    const messagesData = await getContactMessages(activeChatId, 1);

                    if (!isCurrent) return;

                    

                    const results = Array.isArray(messagesData.results) ? messagesData.results : messagesData;

                    const nextLink = messagesData.next || null;

                    const reversedMessages = [...results].reverse();



                    const firstUnreadIndex = getFirstUnreadMessageIndex(reversedMessages);

                    const selectedContactResolved = contactsRef.current.find(contact => String(contact.id) === String(activeChatId)) || selectedContact;

                    setUnreadAnchorId(firstUnreadIndex >= 0 ? reversedMessages[firstUnreadIndex]?.id : null);

                    setPendingReadContact(firstUnreadIndex >= 0 ? selectedContactResolved : null);

                    setNewMessagesNotice(0);

                    setMessages(reversedMessages);

                    

                    setHasMoreMessages(!!nextLink);

                    setCurrentPage(1);

                } catch (err) {

                    const errorMsg = getApiErrorMessage(err, 'Failed to load messages');

                    setToast({ type: 'error', message: errorMsg });

                }

            };

            loadMessages();

            return () => {

                isCurrent = false;

            };

        } else {

            setMessages([]);

            setUnreadAnchorId(null);

            setPendingReadContact(null);

            setNewMessagesNotice(0);

        }

    }, [activeChatId, activeConfigId]);



    // IntersectionObserver to mark unread messages as read when visible in viewport

    useEffect(() => {

        const scrollContainer = messagesEndRef.current;

        if (!scrollContainer || !activeChatId) return;



        const handleIntersection = (entries) => {

            let hasNewReads = false;

            

            entries.forEach(entry => {

                if (entry.isIntersecting && document.visibilityState === 'visible') {

                    const messageId = entry.target.getAttribute('data-message-id');

                    if (messageId) {

                        hasNewReads = true;

                        // Mark as read locally in messages state

                        setMessages(prevMessages =>

                            prevMessages.map(msg => {

                                if (String(msg.id) === String(messageId)) {

                                    return {

                                        ...msg,

                                        read: true,

                                        is_read: true,

                                        read_at: msg.read_at || new Date().toISOString(),

                                        status: 'Read',

                                    };

                                }

                                return msg;

                            })

                        );

                    }

                }

            });



            if (hasNewReads) {

                // Trigger backend read sync for this contact

                const activeContact = contactsRef.current.find(l => String(l.id) === String(activeChatId));

                if (activeContact) {

                    clearUnreadForContact(activeContact);

                }

            }

        };



        const observer = new IntersectionObserver(handleIntersection, {

            root: scrollContainer,

            threshold: 0.15, // Intersects if 15% visible

        });



        // Query all unread incoming elements

        const unreadElements = scrollContainer.querySelectorAll('.unread-incoming-message');

        unreadElements.forEach(el => observer.observe(el));



        return () => {

            observer.disconnect();

        };

    }, [activeChatId, messages, clearUnreadForContact]);



    // Ensure we clear notifications if we open a chat that has unread count or pending notifications

    useEffect(() => {

        if (!activeChatId || !contactsRef.current) return;

        const activeContact = contactsRef.current.find(l => String(l.id) === String(activeChatId));

        if (activeContact) {

            const normalizePhone = (p) => (p || '').replace(/\D/g, '').slice(-10);

            const contactPhone10 = normalizePhone(activeContact.phone_number || activeContact.phone);

            const hasPendingNotifs = notifications && notifications.some(n => !n.is_read && normalizePhone(n.contact_phone) === contactPhone10);

            

            if (activeContact.unread_count > 0 || hasPendingNotifs) {

                clearUnreadForContact(activeContact);

            }

        }

    }, [activeChatId, notifications, clearUnreadForContact]);



    // Find the currently selected chat object

    const activeChat = contacts.find(c => c.id === activeChatId) || contacts[0];

    

    // Calculate if the 24-hour customer service window is open

    const isWithin24hWindow = Boolean(activeChat?.is_within_24h_window);



    const handleInitiateVideoCall = async () => {

        const phone = activeChat?.phone || activeChat?.phone_number;

        if (!phone) {

            setToast({ type: 'error', message: 'No contact selected or contact lacks phone number.' });

            return;

        }

        setIsVideoLoading(true);

        try {

            const response = await apiClient.post('/video-call/initiate/', {

                phone_number: phone

            });

            setToast({ type: 'success', message: 'Video call link generated and sent to customer!' });

            if (response.data && response.data.room_url) {

                window.open(response.data.room_url, '_blank');

            }

        } catch (err) {

            console.error('Failed to initiate video call:', err);

            const errMsg = getApiErrorMessage(err, 'Failed to initiate video call.');

            setToast({ type: 'error', message: errMsg });

        } finally {

            setIsVideoLoading(false);

        }

    };
 
 
 
    const handleToggleAiMode = async () => {
        if (!activeChatId || !activeChat) return;

        const currentEscalated = activeChat.attributes?.escalated === true;
        const nextEscalated = !currentEscalated;
        
        const updatedAttributes = {
            ...(activeChat.attributes || {}),
            escalated: nextEscalated
        };

        try {
            await updateContact(activeChatId, { attributes: updatedAttributes });
            
            // Update local state for contacts
            setContacts(prev => prev.map(c => 
                String(c.id) === String(activeChatId) 
                    ? { ...c, attributes: updatedAttributes } 
                    : c
            ));
            
            setToast({
                type: 'success',
                message: nextEscalated 
                    ? 'AI Bot disabled. Chat set to manual.' 
                    : 'AI Bot enabled. Chat set to automatic.'
            });
        } catch (err) {
            console.error("Failed to toggle AI mode", err);
            setToast({ type: 'error', message: 'Failed to toggle AI mode. Please try again.' });
        }
    };



    const handleSaveName = async () => {

        if (!activeChatId) return;

        

        const newName = editNameValue.trim() || 'Unknown';

        

        if (newName === activeChatName || (newName === 'Unknown' && !activeChatHasRealName)) {

            setIsEditingName(false);

            return;

        }

        try {

            await updateContact(activeChatId, { name: newName });

            setContacts(prev => prev.map(l => String(l.id) === String(activeChatId) ? { ...l, name: newName } : l));

            setIsEditingName(false);

            setToast({ type: 'success', message: 'Name updated successfully.' });

        } catch (err) {

            setToast({ type: 'error', message: getApiErrorMessage(err, 'Failed to update name') });

        }

    };



    const handleTouchStart = (contactId) => {

        if (isSelectionMode) return;

        longPressTriggeredRef.current = false;

        longPressTimerRef.current = setTimeout(() => {

            longPressTriggeredRef.current = true;

            setIsSelectionMode(true);

            setSelectedContactIds(new Set([contactId]));

            navigator.vibrate?.(50);

        }, 500);

    };



    const handleTouchEnd = () => {

        if (longPressTimerRef.current) {

            clearTimeout(longPressTimerRef.current);

        }

    };



    const toggleContactSelection = (contactId) => {

        const newSelected = new Set(selectedContactIds);

        if (newSelected.has(contactId)) {

            newSelected.delete(contactId);

            if (newSelected.size === 0) setIsSelectionMode(false);

        } else {

            newSelected.add(contactId);

        }

        setSelectedContactIds(newSelected);

    };



    const handleSelectAll = () => {

        if (selectedContactIds.size === filteredContacts.length) {

            setSelectedContactIds(new Set());

            setIsSelectionMode(false);

        } else {

            setSelectedContactIds(new Set(filteredContacts.map(l => l.id)));

        }

    };



    const handleBulkDelete = async () => {

        if (selectedContactIds.size === 0) return;

        try {

            await Promise.all(Array.from(selectedContactIds).map(id => deleteContact(id)));

            setContacts(prev => prev.filter(l => !selectedContactIds.has(l.id)));

            if (selectedContactIds.has(activeChatId)) {

                setActiveChatId(null);

            }

            setIsSelectionMode(false);

            setSelectedContactIds(new Set());

            setShowBulkDeleteConfirm(false);

            setToast({ type: 'delete', message: 'Selected contacts deleted successfully.' });

        } catch (err) {

            setToast({ type: 'error', message: getApiErrorMessage(err, 'Failed to delete some contacts') });

            setShowBulkDeleteConfirm(false);

        }

    };

    const handleBulkMarkRead = async () => {
        if (selectedContactIds.size === 0) return;
        const ids = Array.from(selectedContactIds);
        try {
            await markInboxRead(ids);
            setContacts(prev => prev.map(c => selectedContactIds.has(c.id) ? { ...c, unread_count: 0 } : c));
            setSelectedContactIds(new Set());
            setIsSelectionMode(false);
            setToast({ type: 'success', message: `Marked ${ids.length} chats as read!` });
        } catch (err) {
            setToast({ type: 'error', message: 'Failed to mark chats as read.' });
        }
    };

    const handleBulkMarkUnread = async () => {
        if (selectedContactIds.size === 0) return;
        const ids = Array.from(selectedContactIds);
        try {
            await markInboxUnread(ids);
            setContacts(prev => prev.map(c => selectedContactIds.has(c.id) ? { ...c, unread_count: Math.max(1, (c.unread_count || 0) + 1) } : c));
            setSelectedContactIds(new Set());
            setIsSelectionMode(false);
            setToast({ type: 'success', message: `Marked ${ids.length} chats as unread!` });
        } catch (err) {
            setToast({ type: 'error', message: 'Failed to mark chats as unread.' });
        }
    };

    const handleSingleMarkUnread = async (e, contact) => {
        e.stopPropagation();
        try {
            await markInboxUnread(contact.id);
            setContacts(prev => prev.map(c => c.id === contact.id ? { ...c, unread_count: Math.max(1, (c.unread_count || 0) + 1) } : c));
            setToast({ type: 'success', message: `Marked conversation with ${contact.name || contact.phone_number} as unread.` });
        } catch (err) {
            setToast({ type: 'error', message: 'Failed to mark as unread.' });
        }
    };

    const handleSingleMarkRead = async (e, contact) => {
        if (e) e.stopPropagation();
        try {
            await markInboxRead(contact.id);
            setContacts(prev => prev.map(c => c.id === contact.id ? { ...c, unread_count: 0 } : c));
            setToast({ type: 'success', message: `Marked conversation with ${contact.name || contact.phone_number} as read.` });
        } catch (err) {
            setToast({ type: 'error', message: 'Failed to mark as read.' });
        }
    };

    const handleCloseChat = () => {
        setActiveChatId(null);
        setMessages([]);
        setShowContactPanel(false);
        setShowContactMenu(false);
        setContextMenu(null);
        setInputText('');
        setSelectedTemplateForSend(null);
        setUnreadAnchorId(null);
        setPendingReadContact(null);
    };

    const handleContextMenu = (e, contact) => {
        if (!contact) return;
        e.preventDefault();
        e.stopPropagation();
        const menuWidth = 220;
        const menuHeight = 220;
        const x = Math.min(e.clientX, window.innerWidth - menuWidth - 10);
        const y = Math.min(e.clientY, window.innerHeight - menuHeight - 10);
        setContextMenu({ x, y, contact });
        setShowContactMenu(false);
    };



    const handleDeleteContact = async () => {

        if (!activeChatId) return;

        try {

            await deleteContact(activeChatId);

            setContacts(prev => prev.filter(l => String(l.id) !== String(activeChatId)));

            setActiveChatId(null);

            setShowDeleteConfirm(false);

            setToast({ type: 'delete', message: 'Contact deleted successfully.' });

        } catch (err) {

            setToast({ type: 'error', message: getApiErrorMessage(err, 'Failed to delete contact') });

        }

    };



    const handleAddContact = async (e) => {

        e.preventDefault();



        if (!newContactPhone.trim()) {

            setToast({ type: 'error', message: 'Phone number is required.' });

            return;

        }



        try {

            const rawPhone = newContactPhone.trim().replace(/^\+/, '');

            const finalPhone = rawPhone.startsWith(countryCode.replace(/^\+/, ''))

                ? `+${rawPhone}`

                : `${countryCode}${rawPhone}`;



            const createdContact = await createContact({

                name: newContactName.trim() || undefined,

                phone_number: finalPhone,

                language: 'en',

                attributes: {

                    language: 'en',

                    name: newContactName.trim() || undefined,

                    phone: finalPhone,

                },

            });



            setContacts((prevContacts) => [createdContact, ...prevContacts]);

            setActiveChatId(createdContact.id);

            setShowAddContact(false);

            setNewContactName('');

            setNewContactPhone('');



            setToast({ type: 'success', message: 'Contact added successfully!' });

        } catch (err) {

            const errorMsg = getApiErrorMessage(err, 'Failed to add contact');

            setToast({ type: 'error', message: errorMsg });

        }

    };



    const handleSendMessage = async () => {

        if ((!inputText.trim() && !selectedFile) || !activeChatId || isSending) return;



        const currentInput = inputText; // Capture current input

        const currentFile = selectedFile;



        if (selectedTemplateForSend) {
            const template = selectedTemplateForSend;
            const components = [];
            const headerText = template.header_text || '';
            const bodyText = template.body || '';
            
            const extractVarKeys = (text) => {
                const regex = /\{\{(\w+)\}\}/g;
                const keys = [];
                let match;
                while ((match = regex.exec(text)) !== null) {
                    keys.push(match[1]);
                }
                return [...new Set(keys)];
            };

            const headerVarKeys = extractVarKeys(headerText);
            const bodyVarKeys = extractVarKeys(bodyText);

            if (headerVarKeys.length > 0) {
                components.push({
                    type: 'header',
                    parameters: headerVarKeys.map((key) => ({
                        type: 'text',
                        parameter_name: key,
                        text: String(template.variableValues[key] || ''),
                    }))
                });
            }

            if (bodyVarKeys.length > 0) {
                components.push({
                    type: 'body',
                    parameters: bodyVarKeys.map((key) => ({
                        type: 'text',
                        parameter_name: key,
                        text: String(template.variableValues[key] || ''),
                    }))
                });
            }



            // Create FormData for proper multipart/form-data transmission

            const formData = new FormData();

            

            // Send phone_number instead of contact_id

            const contactPhoneRaw = activeChat?.phone || activeChat?.phone_number || '';

            formData.append('phone_number', contactPhoneRaw);

            

            formData.append('template_name', template.template_name || template.name);

            formData.append('language_code', template.language_code || template.language || 'en_US');

            if (components.length > 0) {

                formData.append('components', JSON.stringify(components));

            }

            if (template.buttonActions) {

                formData.append('button_actions', JSON.stringify(template.buttonActions));

            }

            if (currentInput) {

                formData.append('text', currentInput);

            }



            // Append media file if present (actual File object, not Data URL)

            if (template.headerMediaFile && template.headerMediaFile instanceof File) {

                formData.append('header_media', template.headerMediaFile, template.headerMediaFile.name);

            }



            try {

                setIsSending(true);

                await sendWhatsAppTemplate(formData);

                setContacts((prevContacts) => sortContactsByLatest(upsertContactList(prevContacts, activeChat || { id: activeChatId }, {

                    last_message: currentInput,

                    last_message_time: new Date().toISOString(),

                    unread_count: 0,

                })));

                setInputText('');

                setSelectedTemplateForSend(null);

                setToast({ type: 'success', message: 'Template sent successfully!' });

                

                // Auto-scroll to latest message on template send

                requestAnimationFrame(() => {

                    scrollChatToBottom('smooth');

                });

            } catch (err) {

                const errorMsg = getApiErrorMessage(err, 'Failed to send template');

                setToast({ type: 'error', message: errorMsg });

            } finally {

                setIsSending(false);

            }

            return;

        }



        if (!isWithin24hWindow) {

            setToast({

                type: 'error',

                message: '24-hour window closed. Please send an approved Template to reopen the conversation.',

            });

            return;

        }



        try {

            setIsSending(true);

            let sentMessage;

            if (currentFile) {

                const formData = new FormData();

                formData.append('contact_id', activeChatId);

                if (currentInput) formData.append('message', currentInput);

                formData.append('file', currentFile);

                sentMessage = await createMessage(formData);

            } else {

                const newMessagePayload = {

                    contact: activeChatId,

                    text: currentInput,

                    sender: 'sent'

                };

                sentMessage = await createMessage(newMessagePayload);

            }



            setMessages((prevMessages) => upsertMessageList(prevMessages, sentMessage));

            setContacts((prevContacts) => sortContactsByLatest(upsertContactList(prevContacts, activeChat || { id: activeChatId }, {

                last_message: sentMessage.text || sentMessage.caption || (currentFile ? `[Attachment: ${currentFile.name}]` : currentInput),

                last_message_time: sentMessage.timestamp || sentMessage.created_at || new Date().toISOString(),

                unread_count: 0,

            })));



            setInputText('');

            setSelectedFile(null);

            setFilePreviewUrl(null);

            if (fileInputRef.current) fileInputRef.current.value = '';

            setToast({ type: 'success', message: currentFile ? 'File sent successfully!' : 'Message sent!' });

            

            // Auto-scroll to latest message on send

            requestAnimationFrame(() => {

                scrollChatToBottom('smooth');

            });

        } catch (err) {

            const errorMsg = getApiErrorMessage(err, 'Failed to send message');

            setToast({ type: 'error', message: errorMsg });

        } finally {

            setIsSending(false);

        }

    };



    const handleSelectTemplate = (template) => {

        if (!activeChatId || isSending) return;

        setInputText(template.processedBody);

        setSelectedTemplateForSend(template);

    };



    const contactStats = useMemo(() => {
        let unreadCount = 0;
        let needsReplyCount = 0;
        let active24hCount = 0;
        let expired24hCount = 0;
        let campaignRepliesCount = 0;
        let leadsCount = 0;
        let directCount = 0;

        contacts.forEach(c => {
            if (c.unread_count > 0) unreadCount++;

            const isIncoming = c.last_message_direction === 'INCOMING' || (c.last_customer_message_time && (!c.last_message_time || new Date(c.last_customer_message_time).getTime() >= new Date(c.last_message_time).getTime() - 5000));
            if (isIncoming) needsReplyCount++;

            if (c.is_within_24h_window) active24hCount++;
            else expired24hCount++;

            if (c.has_campaign_reply || (c.source === 'campaign' && c.last_customer_message_time)) {
                campaignRepliesCount++;
            }
            if (c.source === 'leads_table' || (c.attributes && (c.attributes.source === 'leads_table' || c.attributes.lead_id))) {
                leadsCount++;
            } else if (c.source === 'direct' || (!c.campaigns?.length && !c.campaign_name)) {
                directCount++;
            }
        });

        return {
            total: contacts.length,
            unreadCount,
            needsReplyCount,
            active24hCount,
            expired24hCount,
            campaignRepliesCount,
            leadsCount,
            directCount,
        };
    }, [contacts]);

    const filteredContacts = useMemo(() => contacts.filter((contact) => {
        const contactName = contact?.name || '';
        const contactPhone = contact?.phone_number || '';
        const lastMsg = contact?.last_message || '';
        const normalizedSearch = searchTerm.trim().toLowerCase();

        if (normalizedSearch) {
            const matches = contactName.toLowerCase().includes(normalizedSearch) ||
                contactPhone.toLowerCase().includes(normalizedSearch) ||
                lastMsg.toLowerCase().includes(normalizedSearch);
            if (!matches) return false;
        }

        if (hideNoMessages) {
            const hasMsg = contact.last_message && contact.last_message.trim().length > 0;
            if (!hasMsg) return false;
        }

        if (activeSourceTab === 'campaign_replies') {
            const isCampaignReply = contact.has_campaign_reply || (contact.source === 'campaign' && contact.last_customer_message_time);
            if (!isCampaignReply) return false;
        } else if (activeSourceTab === 'leads') {
            const isLead = contact.source === 'leads_table' || (contact.attributes && (contact.attributes.source === 'leads_table' || contact.attributes.lead_id));
            if (!isLead) return false;
        } else if (activeSourceTab === 'direct') {
            const isLead = contact.source === 'leads_table' || (contact.attributes && (contact.attributes.source === 'leads_table' || contact.attributes.lead_id));
            const isCampaign = contact.source === 'campaign' || contact.campaign_name;
            if (isLead || isCampaign) return false;
        }

        if (activeStatusFilter === 'unread') {
            if (!(contact.unread_count > 0)) return false;
        } else if (activeStatusFilter === 'needs_reply') {
            const isIncoming = contact.last_message_direction === 'INCOMING' || (contact.last_customer_message_time && (!contact.last_message_time || new Date(contact.last_customer_message_time).getTime() >= new Date(contact.last_message_time).getTime() - 5000));
            if (!isIncoming) return false;
        } else if (activeStatusFilter === 'active_24h') {
            if (!contact.is_within_24h_window) return false;
        } else if (activeStatusFilter === 'expired_24h') {
            if (contact.is_within_24h_window) return false;
        }

        return true;
    }), [contacts, searchTerm, activeSourceTab, activeStatusFilter, hideNoMessages]);



    const handleAttachClick = () => {

        if (fileInputRef.current) {

            fileInputRef.current.click();

        }

    };



    const handleFileChange = (e) => {

        const file = e.target.files[0];

        if (file) {

            setSelectedFile(file);

            if (file.type.startsWith('image/')) {

                setFilePreviewUrl(URL.createObjectURL(file));

            } else {

                setFilePreviewUrl(null);

            }

        }

    };



    const activeChatPhoneRaw = activeChat?.phone || activeChat?.phone_number || '';

    const activeChatPhone10Digit = activeChatPhoneRaw.replace(/\D/g, '').slice(-10);



    let activeChatRawName = (activeChat?.name && activeChat.name !== 'Unknown') ? activeChat.name : '';

    let activeChatNameDigits = activeChatRawName.replace(/\D/g, '');

    const activeChatHasRealName = activeChatRawName && activeChatNameDigits.length < 8;



    const activeChatName = activeChatHasRealName 

        ? activeChatRawName 

        : (activeChatPhone10Digit || activeChatPhoneRaw || 'No contact selected');



    const activeChatAvatar = activeChatHasRealName

        ? activeChatRawName.substring(0, 2).toUpperCase()

        : (

            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>

                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>

                <circle cx="12" cy="7" r="4"></circle>

            </svg>

        );



    const activeChatPhone = activeChatPhoneRaw || 'N/A';

    const formatClassification = (contactType) => {

        if (!contactType) return 'Unset';

        return contactType.charAt(0).toUpperCase() + contactType.slice(1).toLowerCase();

    };

    const formatSidebarTime = (value) => {

        return formatSidebarTimestamp(value);

    };

    const activeChatDisplayName = activeChatName;



    const getMessageBody = (msg) => {

        const body = msg?.text || msg?.message || msg?.caption || msg?.media_url || '';

        if (body.trim()) return body;

        return msg?.type && msg.type !== 'text' ? `[${msg.type} message]` : '[empty message]';

    };



    // WhatsApp-style template buttons at the bottom of the card

    const renderMessageButtons = (msg, isSent) => {

        if (!msg.buttons || msg.buttons.length === 0) return null;



        return (

            <div className="msg-buttons-container">

                {msg.buttons.map((btn, idx) => {

                    const isString = typeof btn === 'string';

                    const btnText = isString ? btn : (btn.text || btn.title || 'Button');

                    const btnType = isString ? 'QUICK_REPLY' : (btn.type || 'QUICK_REPLY');

                    const btnUrl = !isString ? btn.url : null;

                    

                    return (

                        <button

                            key={idx}

                            className={`msg-wa-button ${isSent ? 'sent' : 'received'}`}

                            onClick={() => {

                                if (btnUrl) window.open(btnUrl, '_blank');

                            }}

                        >

                            {btnType === 'URL' && (

                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>

                            )}

                            {btnType === 'QUICK_REPLY' && (

                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 17 4 12 9 7"></polyline><path d="M20 18v-2a4 4 0 0 0-4-4H4"></path></svg>

                            )}

                            {btnType === 'PHONE_NUMBER' && (

                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72"></path></svg>

                            )}

                            <span>{btnText}</span>

                        </button>

                    );

                })}

            </div>

        );

    };



    // Rich message content renderer

    const renderMessageContent = (msg) => {

        const msgType = (msg.type || 'text').toLowerCase();

        const mediaUrl = getRenderableMediaUrl(msg.media_url);

        

        let detectedType = msgType;

        if (msgType === 'template' && mediaUrl) {

            detectedType = getMediaTypeFromUrl(mediaUrl);

        }



        const hasMedia = detectedType === 'image' || detectedType === 'video';

        const isOutgoing = msg.direction === 'OUTGOING' || msg.sender !== 'customer';



        return (

            <>

                {/* Header media */}

                {detectedType === 'image' && mediaUrl && (

                    <div className="msg-media-wrapper">

                        <img

                            src={mediaUrl}

                            alt="Shared image"

                            className="msg-media-image"

                            loading="lazy"

                            onClick={() => window.open(mediaUrl, '_blank')}

                            onLoad={() => {

                                if (isNearBottomRef.current) {

                                    scrollChatToBottom('auto');

                                }

                            }}

                        />

                    </div>

                )}

                {detectedType === 'video' && mediaUrl && (

                    <div className="msg-media-wrapper">

                        <video

                            src={mediaUrl}

                            className="msg-media-video"

                            controls

                            preload="metadata"

                        />

                    </div>

                )}

                {detectedType === 'document' && mediaUrl && (

                    <a href={mediaUrl} target="_blank" rel="noopener noreferrer" className="msg-document-card">

                        <div className="msg-doc-icon">

                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>

                        </div>

                        <div className="msg-doc-info">

                            <span className="msg-doc-name">{msg.caption || 'Document'}</span>

                            <span className="msg-doc-action">Tap to download</span>

                        </div>

                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>

                    </a>

                )}

                {detectedType === 'audio' && mediaUrl && (

                    <div className="msg-audio-wrapper">

                        <audio src={mediaUrl} controls preload="metadata" style={{ width: '100%', maxWidth: '280px' }} />

                    </div>

                )}



                {/* Message body text with formatting */}

                {detectedType === 'image' && msg.caption ? (

                    <ExpandableMessageText key={`expand-${msg.id}`} text={msg.caption} hasMedia={hasMedia} />

                ) : detectedType === 'document' && !msg.text ? null : (

                    <ExpandableMessageText key={`expand-${msg.id}`} text={getMessageBody(msg)} hasMedia={hasMedia} />

                )}

            </>

        );

    };



    const formatFullTime = (dateStr) => {

        if (!dateStr) return 'N/A';

        const date = new Date(dateStr);

        return date.toLocaleString([], { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short', year: 'numeric' });

    };



    const renderMessageStatus = (msg) => {

        if (msg.sender === 'customer' || msg.direction === 'INCOMING') return null;

        const status = (msg.status || '').toLowerCase();

        const iconStyle = { marginLeft: '4px', display: 'inline-flex', alignItems: 'center' };

        if (status === 'waiting') {

            return (

                <span style={iconStyle} title="Waiting">

                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 12.6A5.6 5.6 0 1 1 8 2.4a5.6 5.6 0 0 1 0 11.2zM8.7 4.5H7.3v4.2l3.4 2.1.7-1.2-2.7-1.6V4.5z" /></svg>

                </span>

            );

        } else if (status === 'failed') {

            return (

                <span style={{ ...iconStyle, color: '#ff4d4d' }} title="Failed">

                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" /></svg>

                </span>

            );

        } else if (status === 'read' || status === 'delivered') {

            const color = status === 'read' ? '#53bdeb' : '#999';

            return (

                <span style={{ ...iconStyle, color }} title={status.charAt(0).toUpperCase() + status.slice(1)}>

                    <svg width="16" height="11" viewBox="0 0 16 11" fill="currentColor"><path d="M11.01 1.102L5.88 6.232l-2.476-2.476L2 5.17l3.88 3.88 6.544-6.544-1.414-1.414zm4 0L5.88 10.232l-4.89-4.89 1.414-1.414 3.476 3.476 7.716-7.716 1.414 1.414z" /></svg>

                </span>

            );

        } else {

            return (

                <span style={{ ...iconStyle, color: '#999' }} title="Sent">

                    <svg width="16" height="11" viewBox="0 0 16 11" fill="currentColor"><path d="M15.01 1.102L5.88 10.232l-4.89-4.89 1.414-1.414 3.476 3.476 7.716-7.716 1.414 1.414z" /></svg>

                </span>

            );

        }

    };



    return (

        <div className="inbox-wrapper">

            {/* Toast Notification */}

            {toast && (

                <Toast

                    message={toast.message}

                    type={toast.type}

                    onClose={() => setToast(null)}

                />

            )}



            {/* Loading State */}

            {loading ? (

                <div style={{ textAlign: 'center', padding: '60px 24px' }}>

                    <h3>Loading inbox...</h3>

                </div>

            ) : (

                <>

                    {error && (

                        <div style={{ padding: '12px 20px', margin: '12px 20px', borderRadius: '10px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444', color: '#dc2626', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>

                            <span>âš ï¸ {error}</span>

                            <button onClick={() => setError(null)} style={{ background: 'transparent', border: 'none', color: '#dc2626', fontWeight: 'bold', cursor: 'pointer' }}>Dismiss</button>

                        </div>

                    )}



                    {/* FULL-WIDTH TOP FILTER & ACTIONS BAR */}
                    <div className="inbox-top-filter-bar">
                        {/* Primary Row: Search + Audience Source Tabs + Top Actions */}
                        <div className="inbox-top-row">
                            <div className="inbox-top-left-group">
                                <div className="inbox-top-search">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-gray)" strokeWidth="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                                    <input
                                        type="text"
                                        placeholder="Search candidate name, phone, message..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <button
                                            type="button"
                                            className="search-clear-btn"
                                            onClick={() => setSearchTerm('')}
                                            title="Clear search"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>

                                <div className="inbox-source-tabs">
                                    <button
                                        className={`source-tab-btn ${activeSourceTab === 'all' ? 'active' : ''}`}
                                        onClick={() => setActiveSourceTab('all')}
                                    >
                                        All <span className="tab-badge">{contactStats.total}</span>
                                    </button>
                                    <button
                                        className={`source-tab-btn ${activeSourceTab === 'campaign_replies' ? 'active' : ''}`}
                                        onClick={() => setActiveSourceTab('campaign_replies')}
                                        title="Candidates who replied to a broadcast campaign"
                                    >
                                        <Megaphone size={13} strokeWidth={2.2} />
                                        Campaign Replies <span className="tab-badge">{contactStats.campaignRepliesCount}</span>
                                    </button>
                                    <button
                                        className={`source-tab-btn ${activeSourceTab === 'leads' ? 'active' : ''}`}
                                        onClick={() => setActiveSourceTab('leads')}
                                        title="Contacts imported or initiated from Leads dashboard"
                                    >
                                        <Target size={13} strokeWidth={2.2} />
                                        Leads <span className="tab-badge">{contactStats.leadsCount}</span>
                                    </button>
                                    <button
                                        className={`source-tab-btn ${activeSourceTab === 'direct' ? 'active' : ''}`}
                                        onClick={() => setActiveSourceTab('direct')}
                                        title="Direct 1-on-1 and organic chats"
                                    >
                                        <User size={13} strokeWidth={2.2} />
                                        Direct <span className="tab-badge">{contactStats.directCount}</span>
                                    </button>
                                </div>
                            </div>

                            <div className="inbox-top-actions">
                                <button
                                    type="button"
                                    className="inbox-add-btn"
                                    onClick={() => setShowAddContact(true)}
                                    title="Add Contact"
                                >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                    Add Contact
                                </button>

                                <button
                                    type="button"
                                    className={`inbox-ai-toggle-btn ${isGlobalAiEnabled ? 'ai-on' : 'ai-off'}`}
                                    onClick={handleToggleGlobalAi}
                                    title={isGlobalAiEnabled ? "Universal AI Auto-Reply is ON. Click to Disable." : "Universal AI Auto-Reply is OFF. Click to Enable."}
                                >
                                    <span className="ai-dot" />
                                    {isGlobalAiEnabled ? 'AI On' : 'AI Off'}
                                </button>
                            </div>
                        </div>

                        {/* Secondary Row: Actionable Status Filter Pills + Hide Empty + Inline Bulk Bar */}
                        <div className="inbox-bottom-row">
                            <div className="inbox-bottom-left-group">
                                <div className="inbox-status-pills">
                                    <button
                                        className={`status-pill-btn ${activeStatusFilter === 'all' ? 'active' : ''}`}
                                        onClick={() => setActiveStatusFilter('all')}
                                    >
                                        All Status
                                    </button>
                                    <button
                                        className={`status-pill-btn pill-unread ${activeStatusFilter === 'unread' ? 'active' : ''}`}
                                        onClick={() => setActiveStatusFilter(activeStatusFilter === 'unread' ? 'all' : 'unread')}
                                    >
                                        <Mail size={12} strokeWidth={2.2} />
                                        Unread <span className="pill-count">{contactStats.unreadCount}</span>
                                    </button>
                                    <button
                                        className={`status-pill-btn ${activeStatusFilter === 'needs_reply' ? 'active' : ''}`}
                                        onClick={() => setActiveStatusFilter(activeStatusFilter === 'needs_reply' ? 'all' : 'needs_reply')}
                                        title="Candidates who sent the last message"
                                    >
                                        <MessageSquareReply size={12} strokeWidth={2.2} />
                                        Needs Reply <span className="pill-count">{contactStats.needsReplyCount}</span>
                                    </button>
                                    <button
                                        className={`status-pill-btn pill-active-24h ${activeStatusFilter === 'active_24h' ? 'active' : ''}`}
                                        onClick={() => setActiveStatusFilter(activeStatusFilter === 'active_24h' ? 'all' : 'active_24h')}
                                        title="Can send direct free-form messages"
                                    >
                                        <Clock size={12} strokeWidth={2.2} />
                                        24h Active <span className="pill-count">{contactStats.active24hCount}</span>
                                    </button>
                                    <button
                                        className={`status-pill-btn pill-expired-24h ${activeStatusFilter === 'expired_24h' ? 'active' : ''}`}
                                        onClick={() => setActiveStatusFilter(activeStatusFilter === 'expired_24h' ? 'all' : 'expired_24h')}
                                        title="Template required to reopen conversation"
                                    >
                                        <AlertCircle size={12} strokeWidth={2.2} />
                                        Expired <span className="pill-count">{contactStats.expired24hCount}</span>
                                    </button>
                                </div>

                                <div className="filter-divider" />

                                <label className="hide-empty-toggle" title="Hide contacts with no candidate replies">
                                    <input
                                        type="checkbox"
                                        checked={hideNoMessages}
                                        onChange={(e) => setHideNoMessages(e.target.checked)}
                                    />
                                    <span>Hide Empty</span>
                                </label>
                            </div>

                            {selectedContactIds.size > 0 ? (
                                <div className="inbox-bulk-bar">
                                    <label className="bulk-select-all-wrap">
                                        <input
                                            type="checkbox"
                                            checked={selectedContactIds.size === filteredContacts.length && filteredContacts.length > 0}
                                            onChange={handleSelectAll}
                                        />
                                        <span>{selectedContactIds.size} Selected</span>
                                    </label>
                                    <div className="bulk-actions-group">
                                        <button className="bulk-action-btn btn-read" onClick={handleBulkMarkRead} title="Mark selected as read">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                            Mark Read
                                        </button>
                                        <button className="bulk-action-btn btn-unread" onClick={handleBulkMarkUnread} title="Mark selected as unread">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                                            Mark Unread
                                        </button>
                                        <button className="bulk-action-btn btn-cancel" onClick={() => setSelectedContactIds(new Set())} title="Clear selection">
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="inbox-showing-count">
                                    Showing <strong>{filteredContacts.length}</strong> of {contacts.length} conversations
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={`inbox-layout ${showContactPanel ? 'panel-open' : ''} ${activeChatId ? 'mobile-chat-active' : 'mobile-list-active'}`}>

                        {/* LEFT PANE: Chat List */}
                        <aside className="chat-sidebar">
                            {/* Clean Header: Title, Count, & Select All */}
                            <div className="chat-sidebar-header">
                                <div className="chat-sidebar-header-title">
                                    <h3>Conversations</h3>
                                    <span className="sidebar-chat-count">{filteredContacts.length}</span>
                                </div>
                                <label className="sidebar-select-all" title="Select all filtered conversations">
                                    <input
                                        type="checkbox"
                                        checked={filteredContacts.length > 0 && selectedContactIds.size === filteredContacts.length}
                                        onChange={handleSelectAll}
                                    />
                                    <span>Select All</span>
                                </label>
                            </div>

                            <div className="chat-list-container">
                                {filteredContacts.length === 0 ? (
                                    <div style={{ padding: '24px', color: 'var(--text-gray)', textAlign: 'center' }}>
                                        No contacts found matching criteria.
                                    </div>
                                ) : filteredContacts.map(contact => {
                                    const hasUnread = contact.unread_count > 0;
                                    const rawPhone = contact?.phone_number || contact?.phone || '';
                                    let cleanPhone = rawPhone.replace(/\D/g, '');
                                    if (cleanPhone.length > 10) cleanPhone = cleanPhone.slice(-10);

                                    let rawName = contact?.name && contact.name !== 'Unknown' ? contact.name : '';
                                    let nameDigits = rawName.replace(/\D/g, '');
                                    const hasRealName = rawName && nameDigits.length < 8;

                                    let primaryText = hasRealName ? rawName : (cleanPhone || rawPhone || 'Unknown');
                                    let secondaryText = contact.last_message ? contact.last_message : (hasRealName ? (cleanPhone || rawPhone) : 'No messages');
                                    let avatarLetters = hasRealName ? rawName.substring(0, 2).toUpperCase() : null;
                                    const isSelected = selectedContactIds.has(contact.id);

                                    return (
                                        <div
                                            key={contact.id}
                                            className={`chat-list-item ${activeChatId === contact.id ? 'active' : ''} ${hasUnread ? 'has-unread' : ''} ${isSelected ? 'selected-mode-item' : ''}`}
                                            onClick={() => {
                                                if (activeChatId !== contact.id) {
                                                    setActiveChatId(contact.id);
                                                    setMessages([]);
                                                }
                                                setUnreadAnchorId(null);
                                                setPendingReadContact(null);
                                                setNewMessagesNotice(0);
                                                setInputText('');
                                                setSelectedTemplateForSend(null);
                                            }}
                                            onContextMenu={(e) => handleContextMenu(e, contact)}
                                        >
                                            {/* Permanent Selection Checkbox */}
                                            <div className="contact-select-wrap" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleContactSelection(contact.id)}
                                                    title="Select chat"
                                                />
                                            </div>

                                            <div className="chat-item-avatar-wrap">
                                                <div className="chat-item-avatar">
                                                    {avatarLetters || (
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                                            <circle cx="12" cy="7" r="4"></circle>
                                                        </svg>
                                                    )}
                                                </div>
                                                {/* 24-Hour Customer Service Window Dot Indicator */}
                                                <span
                                                    className={`window-indicator-dot ${contact.is_within_24h_window ? 'window-active' : 'window-expired'}`}
                                                    title={contact.is_within_24h_window ? '24h Service Window Active' : '24h Window Expired'}
                                                />
                                            </div>

                                            <div className="chat-item-details">
                                                <div className="chat-item-header">
                                                    <span className={`chat-item-name ${hasUnread ? 'unread-name' : ''}`}>
                                                        {primaryText}
                                                    </span>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <span className={`chat-item-time ${hasUnread ? 'unread-time' : ''}`}>
                                                            {formatSidebarTime(contact.last_message_time || contact.created_at)}
                                                        </span>
                                                        {/* Quick Hover Mark as Unread Action */}
                                                        <div className="contact-quick-actions" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                type="button"
                                                                className="quick-action-icon-btn"
                                                                onClick={(e) => handleSingleMarkUnread(e, contact)}
                                                                title="Mark as unread"
                                                            >
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="chat-item-bottom-row">
                                                    <span className="chat-item-preview">{secondaryText}</span>
                                                    {hasUnread && (
                                                        <span className="unread-badge">
                                                            {contact.unread_count > 99 ? '99+' : contact.unread_count}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Contact Card Status Badges (Campaign / Leads / Needs Reply / 24h) */}
                                                <div className="chat-item-tags-row">
                                                    {contact.has_campaign_reply ? (
                                                        <span className="contact-tag tag-campaign-reply" title="Replied to Broadcast Campaign">
                                                            <Megaphone size={10} strokeWidth={2.2} /> Campaign Reply
                                                        </span>
                                                    ) : (contact.source === 'leads_table' || (contact.attributes && contact.attributes.source === 'leads_table')) ? (
                                                        <span className="contact-tag tag-lead" title="Imported from Candidate Leads">
                                                            <Target size={10} strokeWidth={2.2} /> Lead
                                                        </span>
                                                    ) : null}

                                                    {contact.last_message_direction === 'INCOMING' && (
                                                        <span className="contact-tag tag-needs-reply" title="Candidate sent last message">
                                                            <MessageSquareReply size={10} strokeWidth={2.2} /> Candidate Replied
                                                        </span>
                                                    )}

                                                    {contact.is_within_24h_window ? (
                                                        <span className="contact-tag tag-24h-active" title="24h Window Active">
                                                            <Clock size={10} strokeWidth={2.2} /> 24h
                                                        </span>
                                                    ) : (
                                                        <span className="contact-tag tag-24h-expired" title="24h Window Expired">
                                                            <AlertCircle size={10} strokeWidth={2.2} /> Expired
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </aside>



                        {/* MIDDLE PANE: Chat Window */}

                        <section className="chat-window">
                            {!activeChatId ? (
                                <div className="chat-window-empty-state">
                                    <div className="empty-state-card">
                                        <div className="empty-state-icon">
                                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                                            </svg>
                                        </div>
                                        <h3>WhatsApp Candidate Inbox</h3>
                                        <p>Select any conversation from the list to view candidate messages, review resume inquiries, or send templates.</p>
                                        <div className="empty-state-quick-tips">
                                            <div className="tip-chip" onClick={() => setActiveStatusFilter('unread')}>
                                                <Mail size={12} strokeWidth={2.2} style={{ color: '#d97706' }} /> Unread ({contactStats.unreadCount})
                                            </div>
                                            <div className="tip-chip" onClick={() => setActiveSourceTab('campaign_replies')}>
                                                <Megaphone size={12} strokeWidth={2.2} style={{ color: '#8b5cf6' }} /> Campaign Replies ({contactStats.campaignRepliesCount})
                                            </div>
                                            <div className="tip-chip" onClick={() => setActiveStatusFilter('active_24h')}>
                                                <Clock size={12} strokeWidth={2.2} style={{ color: '#10b981' }} /> Active 24h ({contactStats.active24hCount})
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Redesigned Header to match reference image (No floating) */}
                                    <div 
                                        className="chat-window-header" 
                                        onContextMenu={(e) => handleContextMenu(e, activeChat)}
                                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                    >

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>

                                            {/* Mobile Back Button */}

                                            <button 

                                                className="mobile-back-btn" 

                                                onClick={() => setActiveChatId(null)}

                                                aria-label="Back to chat list"

                                            >

                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">

                                                    <line x1="19" y1="12" x2="5" y2="12"></line>

                                                    <polyline points="12 19 5 12 12 5"></polyline>

                                                </svg>

                                            </button>



                                                                            {/* Profile Info */}

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer' }} onClick={() => setShowContactPanel(true)}>

                                                <div className="chat-item-avatar" style={{ width: '44px', height: '44px', background: 'var(--brand-purple-light)', color: 'var(--brand-purple)', fontSize: '14px' }}>

                                                    {activeChatAvatar}

                                                </div>

                                                <div style={{ display: 'flex', flexDirection: 'column' }}>

                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>

                                                        <div style={{ fontWeight: '700', fontSize: '18px', color: 'var(--text-dark)' }}>

                                                            {activeChatDisplayName}

                                                        </div>

                                                        {activeChatId && (

                                                            <span style={{ 

                                                                fontSize: '11px', 

                                                                display: 'inline-flex', 

                                                                alignItems: 'center', 

                                                                gap: '4px', 

                                                                padding: '2px 8px', 

                                                                borderRadius: '12px', 

                                                                fontWeight: '600', 

                                                                backgroundColor: isWithin24hWindow ? '#e6f4ea' : '#fce8e6', 

                                                                color: isWithin24hWindow ? '#137333' : '#c5221f' 

                                                            }}>

                                                                {isWithin24hWindow

                                                                    ? <CheckCircle size={12} strokeWidth={2.5} />

                                                                    : <Lock size={12} strokeWidth={2.5} />

                                                                }

                                                                <span>{isWithin24hWindow ? 'Service Window Active' : 'Manual Messaging Locked'}</span>

                                                            </span>

                                                        )}

                                                    </div>

                                                    {activeChatHasRealName && activeChatPhone10Digit && (

                                                        <div style={{ fontSize: '13px', color: 'var(--text-gray)', fontWeight: '500', marginTop: '2px' }}>

                                                            {activeChatPhone10Digit}

                                                        </div>

                                                    )}

                                                </div>

                                            </div>



                                            {/* Bot Status & Mode Toggle */}

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>

                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                                                    <div style={{

                                                        width: '32px',

                                                        height: '32px',

                                                        background: activeChat?.attributes?.escalated === true ? '#6B7280' : '#10B981',

                                                        borderRadius: '8px',

                                                        display: 'flex',

                                                        alignItems: 'center',

                                                        justifyContent: 'center',

                                                        color: 'white',

                                                        fontWeight: '800',

                                                        fontSize: '14px',

                                                        transition: 'background 0.3s'

                                                    }}>{activeChat?.attributes?.escalated === true ? 'M' : 'B'}</div>

                                                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>

                                                        <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-dark)' }}>

                                                            {activeChat?.attributes?.escalated === true ? 'Manual Mode' : 'AI Bot Mode'}

                                                        </span>

                                                        <span style={{ 

                                                            fontSize: '11px', 

                                                            color: activeChat?.attributes?.escalated === true ? '#6B7280' : '#10B981', 

                                                            fontWeight: '600',

                                                            transition: 'color 0.3s'

                                                        }}>

                                                            {activeChat?.attributes?.escalated === true ? 'AI Suspended' : 'Bot Active'}

                                                        </span>

                                                    </div>

                                                </div>



                                                {/* Toggle Button */}

                                                <button 

                                                    onClick={handleToggleAiMode}

                                                    style={{

                                                        display: 'flex',

                                                        alignItems: 'center',

                                                        gap: '6px',

                                                        padding: '6px 12px',

                                                        borderRadius: '20px',

                                                        border: '1.5px solid',

                                                        borderColor: activeChat?.attributes?.escalated === true ? '#f59e0b' : '#10B981',

                                                        backgroundColor: activeChat?.attributes?.escalated === true ? 'rgba(245, 158, 11, 0.08)' : 'rgba(16, 185, 129, 0.08)',

                                                        color: activeChat?.attributes?.escalated === true ? '#d97706' : '#059669',

                                                        fontWeight: '700',

                                                        fontSize: '12px',

                                                        cursor: 'pointer',

                                                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',

                                                        boxShadow: activeChat?.attributes?.escalated === true ? '0 2px 4px rgba(245, 158, 11, 0.1)' : '0 2px 4px rgba(16, 185, 129, 0.1)',

                                                        outline: 'none',

                                                    }}

                                                >

                                                    <div style={{

                                                        width: '8px',

                                                        height: '8px',

                                                        borderRadius: '50%',

                                                        backgroundColor: activeChat?.attributes?.escalated === true ? '#f59e0b' : '#10B981',

                                                        boxShadow: activeChat?.attributes?.escalated === true ? '0 0 6px #f59e0b' : '0 0 6px #10B981',

                                                    }} />

                                                    <span>{activeChat?.attributes?.escalated === true ? 'Switch to Bot' : 'Switch to Manual'}</span>

                                                </button>

                                            </div>



                                        </div>



                                        {/* Contact Actions Menu */}

                                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>

                                            {/* Video Call Button */}

                                            <button

                                                onClick={handleInitiateVideoCall}

                                                disabled={isVideoLoading}

                                                style={{

                                                    background: 'transparent',

                                                    border: 'none',

                                                    cursor: 'pointer',

                                                    padding: '8px',

                                                    color: 'var(--brand-purple)',

                                                    borderRadius: '50%',

                                                    display: 'flex',

                                                    alignItems: 'center',

                                                    justifyContent: 'center',

                                                    marginRight: '8px'

                                                }}

                                                title="Video Call"

                                            >

                                                {isVideoLoading ? (

                                                    <span className="video-spinner" style={{

                                                        width: '16px',

                                                        height: '16px',

                                                        border: '2px solid var(--brand-purple)',

                                                        borderTopColor: 'transparent',

                                                        borderRadius: '50%',

                                                        display: 'inline-block',

                                                        animation: 'spin 1s linear infinite'

                                                    }} />

                                                ) : (

                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">

                                                        <path d="M23 7l-7 5 7 5V7z"></path>

                                                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>

                                                    </svg>

                                                )}

                                            </button>



                                            <button 

                                                onClick={() => setShowContactMenu(!showContactMenu)} 

                                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', color: 'var(--text-gray)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}

                                                title="Contact Actions"

                                            >

                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">

                                                    <circle cx="12" cy="12" r="1"></circle>

                                                    <circle cx="12" cy="5" r="1"></circle>

                                                    <circle cx="12" cy="19" r="1"></circle>

                                                </svg>

                                            </button>

                                            

                                            {showContactMenu && (

                                                <>

                                                    <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setShowContactMenu(false)} />

                                                    <div className="contact-actions-dropdown">
                                                        <button onClick={() => { setShowContactMenu(false); setShowContactPanel(true); }} className="action-item">
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                                                            View Contact
                                                        </button>

                                                        <button onClick={handleCloseChat} className="action-item">
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
                                                            Close Chat
                                                        </button>

                                                        <button onClick={(e) => { setShowContactMenu(false); handleSingleMarkUnread(e, activeChat); }} className="action-item">
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                                                            Mark as Unread
                                                        </button>

                                                        <div style={{ height: '1px', background: 'var(--border-color)', margin: '4px 0' }} />

                                                        <button onClick={() => { setShowContactMenu(false); setToast({ type: 'info', message: 'Contact blocked' }); }} className="action-item">
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></svg>
                                                            Block Contact
                                                        </button>

                                                        <button onClick={() => { setShowContactMenu(false); setShowDeleteConfirm(true); }} className="action-item danger">
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                                                            Delete Contact
                                                        </button>
                                                    </div>

                                                </>

                                            )}

                                        </div>

                                    </div>



                                    {/* Chat History with Date Grouping and Unread Divider */}

                                    <div className="chat-history" ref={messagesEndRef} onScroll={handleChatScroll}>

                                        <MessageGroup

                                            messages={messages}

                                            conversationId={activeChatId}

                                            savedScrollTop={scrollPositionsRef.current[activeChatId]}

                                            renderMessage={(msg) => {

                                                const isIncoming = (msg.direction === 'INCOMING' || msg.sender === 'customer' || msg.sender === 'received') && String(msg.type || '').toLowerCase() !== 'template';

                                                

                                                return (

                                                <div

                                                    key={msg.id}

                                                    className={`message-wrapper ${isIncoming ? 'received' : 'sent'}`}

                                                >

                                                    <div className={`message-container ${

                                                         msg.media_url || 

                                                         (msg.buttons && msg.buttons.length > 0) || 

                                                         ['image', 'video', 'audio', 'document'].includes(String(msg.type || '').toLowerCase())

                                                             ? 'has-media-or-buttons' 

                                                             : ''

                                                     }`}>

                                                        <div className="message-bubble">

                                                            {/* Rich message content (media, text) */}

                                                            {renderMessageContent(msg)}

 

                                                            {/* 3-dot info button — INSIDE bubble for outgoing messages only */}

                                                            {!isIncoming && (

                                                                <button

                                                                    className={`msg-info-trigger ${msg.media_url ? 'msg-info-on-media' : ''}`}

                                                                    onClick={(e) => {

                                                                        e.stopPropagation();

                                                                        setMessageInfo(msg);

                                                                    }}

                                                                    title="Message Info"

                                                                >

                                                                    <MoreVertical size={14} />

                                                                </button>

                                                            )}

 

                                                            {/* Footer: time + status ticks — inline flow, never overflows */}

                                                            <div className="message-status-inside">

                                                                <span>

                                                                    {formatMessageTime(msg.created_at || msg.timestamp)}

                                                                </span>

                                                                {renderMessageStatus(msg)}

                                                            </div>

 

                                                            {/* WhatsApp-style template buttons at the very bottom of the bubble */}

                                                            {renderMessageButtons(msg, !isIncoming)}

                                                        </div>

                                                    </div>

                                                </div>

                                                )}}

                                            containerRef={messagesEndRef}

                                            shouldAutoScroll={true}

                                            unreadAnchorId={unreadAnchorId}

                                            onScrollToUnread={() => {

                                                if (pendingReadContact) scheduleReadAfterScroll(pendingReadContact);

                                            }}

                                            onInitialScrollComplete={(position) => {

                                                if (position === 'bottom' || position === 'restored') {

                                                    setNewMessagesNotice(0);

                                                    isNearBottomRef.current = true;

                                                    setIsNearBottom(true);

                                                }

                                            }}

                                        />

                                        {newMessagesNotice > 0 && !isNearBottom && (

                                            <button

                                                type="button"

                                                className="new-messages-indicator-btn"

                                                onClick={() => {

                                                    scrollChatToBottom('smooth');

                                                    setNewMessagesNotice(0);

                                                    if (activeChat) clearUnreadForContact(activeChat);

                                                }}

                                                aria-label="Scroll to new messages"

                                            >

                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">

                                                    <polyline points="6 9 12 15 18 9"></polyline>

                                                </svg>

                                                <span className="badge">

                                                    {newMessagesNotice}

                                                </span>

                                            </button>

                                        )}

                                     </div>

                                    {/* Warning Banner when locked */}
                                    {!isWithin24hWindow && !selectedTemplateForSend && activeChatId && (
                                        <div className="window-warning-banner" style={{
                                            padding: '10px 16px',
                                            backgroundColor: '#fef2f2',
                                            borderTop: '1px solid #fee2e2',
                                            borderBottom: '1px solid #fee2e2',
                                            color: '#991b1b',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '12px',
                                            flexWrap: 'wrap'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '240px' }}>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                                                <span>
                                                    The 24-hour customer service window has expired. Manual messaging is disabled. Please send an approved WhatsApp Template Message.
                                                </span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setIsTemplateModalOpen(true)}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    padding: '6px 14px',
                                                    backgroundColor: '#2563eb',
                                                    color: '#ffffff',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    boxShadow: '0 1px 3px rgba(37,99,235,0.25)',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
                                                Send Template to Reopen
                                            </button>
                                        </div>
                                    )}



                                    {/* Selected File Attachment Preview */}

                                    {selectedFile && (

                                        <div style={{

                                            display: 'flex',

                                            alignItems: 'center',

                                            justifyContent: 'space-between',

                                            padding: '8px 12px',

                                            marginBottom: '8px',

                                            background: 'var(--surface-white)',

                                            borderRadius: '12px',

                                            border: '1.5px solid var(--brand-purple)',

                                            boxShadow: 'var(--shadow-sm)',

                                            fontSize: '12.5px',

                                            color: 'var(--text-dark)'

                                        }}>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>

                                                {filePreviewUrl ? (

                                                    <img src={filePreviewUrl} alt="preview" style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover' }} />

                                                ) : (

                                                    <span style={{ fontSize: '20px' }}>📎</span>

                                                )}

                                                <div style={{ display: 'flex', flexDirection: 'column' }}>

                                                    <span style={{ fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '240px' }}>

                                                        {selectedFile.name}

                                                    </span>

                                                    <span style={{ color: 'var(--text-gray)', fontSize: '11px' }}>

                                                        {(selectedFile.size / 1024).toFixed(1)} KB

                                                    </span>

                                                </div>

                                            </div>

                                            <button

                                                onClick={() => {

                                                    setSelectedFile(null);

                                                    setFilePreviewUrl(null);

                                                    if (fileInputRef.current) fileInputRef.current.value = '';

                                                }}

                                                style={{

                                                    background: 'rgba(239, 68, 68, 0.1)',

                                                    color: '#ef4444',

                                                    border: 'none',

                                                    borderRadius: '50%',

                                                    width: '24px',

                                                    height: '24px',

                                                    cursor: 'pointer',

                                                    display: 'flex',

                                                    alignItems: 'center',

                                                    justifyContent: 'center',

                                                    fontSize: '14px',

                                                    fontWeight: 'bold'

                                                }}

                                                title="Remove attachment"

                                            >

                                                ✕

                                            </button>

                                        </div>

                                    )}



                                    {/* Input Area */}

                                    <div className={`chat-input-area ${!isWithin24hWindow && !selectedTemplateForSend ? 'locked-wrapper' : ''}`} style={{ display: 'flex', alignItems: 'center' }}>

                                        <input

                                            type="file"

                                            ref={fileInputRef}

                                            style={{ display: 'none' }}

                                            onChange={handleFileChange}

                                        />



                                        {!isWithin24hWindow && !selectedTemplateForSend && (

                                            <div className="input-lock-icon" style={{ paddingLeft: '12px', display: 'flex', alignItems: 'center', color: '#dc2626' }} title="Manual Chat Locked">

                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>

                                            </div>

                                        )}



                                        <input

                                            type="text"

                                            className={`chat-input-field ${!isWithin24hWindow && !selectedTemplateForSend ? 'locked' : ''}`}

                                            placeholder={

                                                !isWithin24hWindow

                                                    ? "24-hour service window expired"

                                                    : "Type a message..."

                                            }

                                            value={inputText}

                                            onChange={(e) => {

                                                setInputText(e.target.value);

                                                if (selectedTemplateForSend) setSelectedTemplateForSend(null);

                                            }}

                                            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}

                                            disabled={!isWithin24hWindow && !selectedTemplateForSend}

                                            readOnly={!isWithin24hWindow && !selectedTemplateForSend}

                                        />



                                        <div className="input-actions">

                                            <button 

                                                className="btn-icon" 

                                                onClick={handleAttachClick} 

                                                title="Attach"

                                                disabled={!isWithin24hWindow && !selectedTemplateForSend}

                                            >

                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>

                                            </button>

                                            <button 

                                                className="btn-icon" 

                                                title="Emoji"

                                                disabled={!isWithin24hWindow && !selectedTemplateForSend}

                                            >

                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><path d="M8 14s1.5 2 4 2 4-2 4-2"></path><line x1="9" y1="9" x2="9.01" y2="9"></line><line x1="15" y1="9" x2="15.01" y2="9"></line></svg>

                                            </button>

                                            <button className="btn-icon" onClick={() => setIsTemplateModalOpen(true)} title="Templates">

                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path></svg>

                                            </button>

                                            <button className="btn-icon" title="Bot">

                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8V4H8"></path><rect width="16" height="12" x="4" y="8" rx="2"></rect><path d="M2 14h2"></path><path d="M20 14h2"></path><path d="M15 13v2"></path><path d="M9 13v2"></path></svg>

                                            </button>



                                            <button className="btn-send" onClick={handleSendMessage} disabled={isSending || (!inputText.trim() && !selectedFile && !selectedTemplateForSend) || (!isWithin24hWindow && !selectedTemplateForSend)}>

                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"></line><polyline points="22 2 15 22 11 13 1 9 22 2"></polyline></svg>

                                            </button>

                                        </div>

                                    </div>
                                </>
                            )}
                        </section>



                        {/* RIGHT PANE: Contact Info Sidebar */}

                        {showContactPanel && (

                            <aside className="contact-info-sidebar">

                                <div className="contact-info-header">

                                    <button className="close-btn" onClick={() => setShowContactPanel(false)}>

                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>

                                    </button>

                                    <h2>Contact info</h2>

                                </div>

                                <div className="contact-info-body">

                                    <div className="contact-info-avatar">

                                        {activeChatAvatar}

                                    </div>

                                    <div className="contact-info-name">

                                        {isEditingName ? (

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', justifyContent: 'center' }}>

                                                <input 

                                                    autoFocus

                                                    className="edit-name-input"

                                                    type="text" 

                                                    value={editNameValue} 

                                                    onChange={e => setEditNameValue(e.target.value)}

                                                    onKeyDown={e => {

                                                        if (e.key === 'Enter') handleSaveName();

                                                        if (e.key === 'Escape') setIsEditingName(false);

                                                    }}

                                                />

                                                <button className="save-name-btn" onClick={handleSaveName}>

                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>

                                                </button>

                                            </div>

                                        ) : (

                                            <>

                                                <span>{activeChatName}</span>

                                                <button className="edit-name-btn" onClick={() => { setIsEditingName(true); setEditNameValue(activeChatName); }} title="Edit Name">

                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>

                                                </button>

                                            </>

                                        )}

                                    </div>

                                    <div className="contact-info-phone">

                                        {activeChatPhone}

                                    </div>

                                </div>

                            </aside>

                        )}



                        {/* ADD CONTACT MODAL (shared component) */}

                        <AddContactModal visible={showAddContact} onClose={() => setShowAddContact(false)} onSaved={(created) => {

                            // If AddContactModal returns the created contact, insert and select it in inbox

                            if (created && created.id) {

                                setContacts((prev) => {

                                    // Use upsertContactList to avoid duplicate if phone already exists

                                    const merged = upsertContactList(prev, created, {});

                                    // If already existed, merged = same length (updated). If new, it was appended.

                                    // Move it to the top if it wasn't there

                                    const idx = merged.findIndex(l => String(l.id) === String(created.id));

                                    if (idx > 0) {

                                        const item = merged.splice(idx, 1)[0];

                                        merged.unshift(item);

                                    }

                                    return merged;

                                });

                                setActiveChatId(created.id);

                            }

                            setShowAddContact(false);

                        }} />

                    </div>

                </>

            )}



            {/* Template Modal */}

            <TemplateModal

                isOpen={isTemplateModalOpen}

                onClose={() => setIsTemplateModalOpen(false)}

                onSelectTemplate={handleSelectTemplate}

                activeContact={activeChat}

            />



            {/* MESSAGE INFO MODAL */}

            {messageInfo && createPortal(

                <div className="wa-module">

                    <div className="wa-modal-overlay" onClick={() => setMessageInfo(null)}>

                        <div className="wa-modal-content info-modal" onClick={e => e.stopPropagation()}>

                            <div className="wa-modal-header">

                                <h2>Message Info</h2>

                                <button className="close-btn" onClick={() => setMessageInfo(null)}><X size={18} /></button>

                            </div>

                        <div className="wa-modal-body" style={{ overflowY: 'auto', flexGrow: 1 }}>

                            <div className="info-preview-msg">

                                <p className="info-preview-text">

                                    "{getMessageBody(messageInfo)}"

                                </p>

                            </div>

                            <div className="status-timeline">

                                <div className="timeline-item active">

                                    <div className="timeline-icon">

                                        <Check size={12} strokeWidth={3} />

                                    </div>

                                    <div className="timeline-content">

                                        <div className="timeline-label">Sent</div>

                                        <div className="timeline-time">{formatFullTime(messageInfo.sent_at || messageInfo.created_at || messageInfo.timestamp)}</div>

                                    </div>

                                </div>

                                <div className={`timeline-item ${messageInfo.delivered_at ? 'active' : 'disabled'}`}>

                                    <div className="timeline-icon">

                                        <CheckCheck size={12} strokeWidth={3} />

                                    </div>

                                    <div className="timeline-content">

                                        <div className="timeline-label">Delivered</div>

                                        <div className="timeline-time">{formatFullTime(messageInfo.delivered_at) || '—'}</div>

                                    </div>

                                </div>

                                <div className={`timeline-item ${messageInfo.read_at ? 'read' : 'disabled'}`}>

                                    <div className="timeline-icon">

                                        <CheckCheck size={12} strokeWidth={3} />

                                    </div>

                                    <div className="timeline-content">

                                        <div className="timeline-label">Read</div>

                                        <div className="timeline-time">{formatFullTime(messageInfo.read_at) || '—'}</div>

                                    </div>

                                </div>

                                {messageInfo.status === 'Failed' && (

                                    <div className="timeline-item failed">

                                        <div className="timeline-icon">

                                            <X size={12} strokeWidth={3} />

                                        </div>

                                        <div className="timeline-content">

                                            <div className="timeline-label" style={{ color: '#ff4d4d' }}>Failed</div>

                                            <div className="timeline-time">{formatFullTime(messageInfo.failed_at) || '—'}</div>

                                        </div>

                                    </div>

                                )}

                            </div>

                        </div>

                    </div>

                </div>

            </div>,

            document.body

        )}



            {/* Bulk Delete Confirmation Modal */}

            {showBulkDeleteConfirm && createPortal(

                <div className="wa-modal-overlay" onClick={() => setShowBulkDeleteConfirm(false)}>

                    <div className="wa-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px', padding: '24px', borderRadius: '24px' }}>

                        <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: '700', color: 'var(--text-dark)' }}>Delete {selectedContactIds.size} Contacts?</h3>

                        <p style={{ color: 'var(--text-gray)', fontSize: '14px', marginBottom: '12px' }}>This action will permanently remove:</p>

                        <ul style={{ color: 'var(--text-gray)', fontSize: '14px', paddingLeft: '20px', marginBottom: '24px', margin: '0 0 24px 20px', padding: 0 }}>

                            <li style={{ marginBottom: '4px' }}>Contact details</li>

                            <li style={{ marginBottom: '4px' }}>Conversation history</li>

                            <li style={{ marginBottom: '4px' }}>Campaign associations</li>

                        </ul>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>

                            <button className="btn-secondary" style={{ borderRadius: '12px', padding: '10px 20px', fontWeight: '600' }} onClick={() => setShowBulkDeleteConfirm(false)}>Cancel</button>

                            <button className="btn-primary" style={{ background: '#ef4444', borderRadius: '12px', padding: '10px 20px', fontWeight: '600', border: 'none', color: 'white' }} onClick={handleBulkDelete}>Delete Contacts</button>

                        </div>

                    </div>

                </div>,

                document.body

            )}



            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && createPortal(
                <div className="wa-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="wa-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px', padding: '24px' }}>
                        <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: '700', color: 'var(--text-dark)' }}>Delete Contact?</h3>
                        <p style={{ color: 'var(--text-gray)', fontSize: '14px', marginBottom: '12px' }}>This action will permanently remove:</p>
                        <ul style={{ color: 'var(--text-gray)', fontSize: '14px', paddingLeft: '20px', marginBottom: '24px' }}>
                            <li>Contact details</li>
                            <li>Conversation history</li>
                            <li>Campaign associations</li>
                        </ul>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button className="btn-secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                            <button className="btn-primary" style={{ background: '#ef4444' }} onClick={handleDeleteContact}>Delete Contact</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Custom Right-Click Context Menu */}
            {contextMenu && createPortal(
                <>
                    <div
                        style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'transparent' }}
                        onClick={() => setContextMenu(null)}
                        onContextMenu={(e) => { e.preventDefault(); setContextMenu(null); }}
                    />
                    <div
                        className="inbox-custom-context-menu"
                        style={{
                            top: `${contextMenu.y}px`,
                            left: `${contextMenu.x}px`,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="action-item"
                            onClick={() => {
                                if (activeChatId !== contextMenu.contact.id) {
                                    setActiveChatId(contextMenu.contact.id);
                                    setMessages([]);
                                }
                                setShowContactPanel(true);
                                setContextMenu(null);
                            }}
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                            View Contact
                        </button>

                        {/* Close Chat (if open) or Open Chat (if different) */}
                        {activeChatId === contextMenu.contact.id ? (
                            <button
                                className="action-item"
                                onClick={handleCloseChat}
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
                                Close Chat
                            </button>
                        ) : (
                            <button
                                className="action-item"
                                onClick={() => {
                                    setActiveChatId(contextMenu.contact.id);
                                    setMessages([]);
                                    setContextMenu(null);
                                }}
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                                Open Chat
                            </button>
                        )}

                        {/* Quick Mark Read / Unread */}
                        <button
                            className="action-item"
                            onClick={(e) => {
                                if (contextMenu.contact.unread_count > 0) {
                                    handleSingleMarkRead(e, contextMenu.contact);
                                } else {
                                    handleSingleMarkUnread(e, contextMenu.contact);
                                }
                                setContextMenu(null);
                            }}
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                            {contextMenu.contact.unread_count > 0 ? 'Mark as Read' : 'Mark as Unread'}
                        </button>

                        <div className="menu-divider" />

                        <button
                            className="action-item"
                            onClick={() => {
                                setToast({ type: 'info', message: `Contact ${contextMenu.contact.name || contextMenu.contact.phone_number} blocked` });
                                setContextMenu(null);
                            }}
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></svg>
                            Block Contact
                        </button>

                        <button
                            className="action-item danger"
                            onClick={() => {
                                setActiveChatId(contextMenu.contact.id);
                                setShowDeleteConfirm(true);
                                setContextMenu(null);
                            }}
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                            Delete Contact
                        </button>
                    </div>
                </>,
                document.body
            )}

        </div>

    );

};



export default InboxView;







